/*eslint sort-keys: ["error", "asc", {caseSensitive: false, natural: true}] -- expect object keys to be sorted*/
/*eslint typescript-sort-keys/interface: ["error", "asc", {"caseSensitive": false, "natural": true,}] -- expect interface keys to be sorted*/

import { isValidCron } from "cron-validator";
import * as R from "runtypes";
import { z } from "zod";

import { NewCsvDownloadConfig } from "./compute/export.js";
import {
  NoPrewarmedImages,
  PrewarmedDefaultInfo,
} from "./customKernels/customKernelTypes.js";
import { DataConnectionType } from "./dataConnectionTypes.js";
import { StringDate } from "./datetimeType.js";
import {
  BasetenEmbeddingModel,
  DEFAULT_MPK,
  EmbeddingModelProviderKeyType,
  LanceEmbeddingModel,
  LanceEmbeddingProvider,
  LanceTableOfHexesSchemaVersionEnum,
  OpenAIEmbeddingModel,
} from "./embeddings.js";
import { CellType } from "./enums.js";
import { ProductionEnvironment } from "./environment.js";
import { HeroPipelineConfig, defaultHeroPipelineConfig } from "./heroTypes.js";
import { OrgId } from "./idTypeBrands.js";
import {
  DefaultKernelManagementCPMigrationState,
  KernelManagementCPMigrationState,
} from "./kernel.js";
import type { FeatureExemptBillingReason as FeatureExemptBillingReasonType } from "./magic/billingTypes.js";
import { FeatureExemptBillingReason } from "./magic/billingTypes.js";
import {
  DEFAULT_MODEL_FEATURE_CONFIG,
  DEFAULT_MODEL_PROVIDER_CONFIG,
} from "./MagicTypes.js";
import {
  FlaggedNewUserFeature,
  FlaggedNewUserFeatureSet,
  NewUserFeatureFlagDefaults,
  isNewUserFeature,
} from "./NewUserFeatures.js";
import { CONSIDER_LAZY_LOADING_SIZE } from "./outputLimits.js";
import { PermissionedEntityTypeName } from "./PermissionedEntityTypeName.js";
import { typedObjectKeys } from "./utils/typedObjects.js";

export const RateLimitConfig = R.Record({
  duration: R.Number,
  points: R.Number,
});
export type RateLimitConfig = R.Static<typeof RateLimitConfig>;

export const RateLimitTargetConfig = R.Partial({
  perClient: RateLimitConfig,
  perEnv: RateLimitConfig,
  perOrg: RateLimitConfig,
});
export type RateLimitTargetConfig = R.Static<typeof RateLimitTargetConfig>;

export const AoRateLimitConfig = R.Partial({
  appSession: RateLimitTargetConfig,
  dataAppFile: RateLimitTargetConfig,
  hex: RateLimitTargetConfig,
  hexVersion: RateLimitTargetConfig,
  orgGuideFiles: RateLimitTargetConfig,
  plainCodeProject: RateLimitTargetConfig,
  reviewRequest: RateLimitTargetConfig,
  semanticProjectVersion: RateLimitTargetConfig,
});
export type AoRateLimitConfig = R.Static<typeof AoRateLimitConfig>;

export const WebsocketRateLimitConfig = R.Partial({
  perClientConnect: RateLimitConfig,
});
export type WebsocketRateLimitConfig = R.Static<
  typeof WebsocketRateLimitConfig
>;

export const OpenAiRateLimitConfig = R.Record({
  requests: R.Dictionary(RateLimitConfig, R.String),
  tokens: R.Dictionary(RateLimitConfig, R.String),
});
export type OpenAiRateLimitConfig = R.Static<typeof OpenAiRateLimitConfig>;
export const DEFAULT_OPEN_AI_RATE_LIMIT_CONFIG: OpenAiRateLimitConfig = {
  requests: {
    "open-ai-rpm-limit": {
      duration: 60,
      points: 400,
    },
  },
  tokens: {
    "open-ai-tpd-limit": {
      duration: 86400,
      points: 160_000_000,
    },
    "open-ai-tpm-limit": {
      duration: 60,
      points: 500_000,
    },
  },
};

export type AgentLlmRetryConfig = {
  /**
   * The coefficient to use for the exponential backoff.
   * A value of 1 means no backoff, a value of 2 means double the delay, etc.
   */
  backoffCoefficient: number;
  baseDelayMs: number;
  /**
   * Controls if and when to fall back to the next provider when retrying
   * "always" -- fall back on any retryable error.
   * "retry-primary-first" -- only fall back on the last attempt, retry primary as much as possible.
   * "never" -- never fall back, always use same provider on retries.
   */
  fallbackStrategy?: "always" | "retry-primary-first" | "never";
  /**
   * The maximum number of *retries* so the total number of
   * calls is `maxRetries` + 1 (the original call).
   */
  maxRetries: number;
};

const DEFAULT_AGENT_LLM_RETRY_CONFIG: AgentLlmRetryConfig = {
  backoffCoefficient: 1,
  baseDelayMs: 0,
  maxRetries: 0,
};

export type AgentWaitForCellConfig = {
  /**
   * How long (ms) cell-running tools wait inline for a cell to finish before
   * returning a "still running" result and letting the agent decide whether
   * to wait (via the WaitForCell tool) or move on.
   */
  "grace-timeout-ms": number;
  /**
   * Whether cell-running agent tools wait inline for cells and expose the
   * WaitForCell tool.
   */
  "wait-for-cell": boolean;
};

const DEFAULT_AGENT_WAIT_FOR_CELL_CONFIG: AgentWaitForCellConfig = {
  "grace-timeout-ms": 20_000,
  "wait-for-cell": false,
};

export type AgentStaleThreadWarningConfig = {
  /** Banner body copy. */
  body: string;
  /**
   * Whether the "continuing this thread may be expensive" warning banner is
   * shown on stale, long threads. Equivalent to the legacy boolean flag.
   */
  enabled: boolean;
  /** Hours of inactivity before a thread is considered stale. */
  hoursOfInactivity: number;
  /** Minimum thread message count before the banner can appear. */
  minMessages: number;
  /** Banner heading. */
  title: string;
};

export const DEFAULT_AGENT_STALE_THREAD_WARNING_CONFIG: AgentStaleThreadWarningConfig =
  {
    enabled: false,
    title: "Start a new thread to save credits",
    body: "Continuing a stale thread replays the full history. Save credits by starting a new thread.",
    minMessages: 25,
    hoursOfInactivity: 24,
  };

/**
 * Default RunSQL tool timeout (in seconds) used when the
 * `agent-run-sql-default-timeout-seconds` flag is unset or malformed. Shared so
 * the tool's fallback and the flag default stay in sync.
 */
export const DEFAULT_RUN_SQL_TIMEOUT_SECONDS = 120;

/**
 * Parses the raw `agent-stale-thread-warning-config` flag value, falling back to the
 * default for any field that is missing, malformed, or the wrong type. A
 * malformed flag therefore degrades gracefully to current behavior instead of
 * breaking the banner.
 */
export function parseAgentStaleThreadWarningConfig(
  raw: unknown,
): AgentStaleThreadWarningConfig {
  if (typeof raw !== "object" || raw == null) {
    return DEFAULT_AGENT_STALE_THREAD_WARNING_CONFIG;
  }
  const record = raw as Record<string, unknown>;
  const bool = (value: unknown, fallback: boolean): boolean =>
    typeof value === "boolean" ? value : fallback;
  const str = (value: unknown, fallback: string): string =>
    typeof value === "string" && value.trim().length > 0 ? value : fallback;
  const num = (value: unknown, fallback: number): number =>
    typeof value === "number" && Number.isFinite(value) ? value : fallback;
  return {
    enabled: bool(
      record.enabled,
      DEFAULT_AGENT_STALE_THREAD_WARNING_CONFIG.enabled,
    ),
    title: str(record.title, DEFAULT_AGENT_STALE_THREAD_WARNING_CONFIG.title),
    body: str(record.body, DEFAULT_AGENT_STALE_THREAD_WARNING_CONFIG.body),
    minMessages: num(
      record.minMessages,
      DEFAULT_AGENT_STALE_THREAD_WARNING_CONFIG.minMessages,
    ),
    hoursOfInactivity: num(
      record.hoursOfInactivity,
      DEFAULT_AGENT_STALE_THREAD_WARNING_CONFIG.hoursOfInactivity,
    ),
  };
}

/**
 * Buckets the OpenAI `prompt_cache_key` so a tenant's load spreads across a
 * bounded pool of warm machines instead of overflowing one host (which causes
 * random fully-uncached iterations). OAI will boot calls to "cold" machines
 * if you make too many requests at once with the same cache key.
 *
 * We construct our prompt cache to bucket based on org/user so as to maximize
 * the amount of the cached prompt we can share between requests. The prompt
 * cache key is constructed as so:
 * `${threadType}_${orgId % orgBucketCount}_${userId % userBucketCount}`
 */
export type AgentPromptCacheRoutingConfig = {
  /** When false, use the legacy model-only key (no behavior change). */
  enabled: boolean;
  /** Org-level bucket count; 1 disables org bucketing */
  orgBucketCount: number;
  /** Per-user bucket count; 1 disables user bucketing. */
  userBucketCount: number;
};

const DEFAULT_AGENT_PROMPT_CACHE_ROUTING_CONFIG: AgentPromptCacheRoutingConfig =
  {
    enabled: false,
    orgBucketCount: 1,
    userBucketCount: 1,
  };

export type ScreenshotMethodName = "browserbase" | "screenshot_service";

export interface DataAppScreenshotStrategy {
  method: ScreenshotMethodName;
}

const DEFAULT_DATA_APP_SCREENSHOT_STRATEGY: DataAppScreenshotStrategy = {
  method: "screenshot_service",
};

export type ConsoleLogMethodName = "browserbase" | "screenshot_service";

export interface DataAppConsoleLogStrategy {
  method: ConsoleLogMethodName;
}

const DEFAULT_DATA_APP_CONSOLE_LOG_STRATEGY: DataAppConsoleLogStrategy = {
  method: "screenshot_service",
};

/** Shared by plain-code-project screenshot + console capture tools. */
export type PlainCodeProjectCaptureStrategy = DataAppScreenshotStrategy;

const DEFAULT_PLAIN_CODE_PROJECT_CAPTURE_STRATEGY: PlainCodeProjectCaptureStrategy =
  {
    method: "browserbase",
  };

const DEFAULT_AO_RATE_LIMIT_CONFIG: AoRateLimitConfig = {};

export const GarbageCollectionRateLimitConfig = R.Record({
  appSessionAoLogsPerMinute: R.Number,
  appSessionEventAppSessions: R.Number,
  appSessionEventLogs: R.Number,
  dataAppFileAoLogsPerMinute: R.Number,
  executionEventsPerMinute: R.Number,
  hexAoLogsPerMinute: R.Number,
  hexVersionAoLogsPerMinute: R.Number,
  OrgGuideFilesAOLogsPerMinute: R.Number,
  outputContentsPerRun: R.Number,
  outputsGracePeriodMinutes: R.Number,
  outputsPerMinute: R.Number,
  projectStatesPerMinute: R.Number,
  queryCacheEntriesPerMinute: R.Number,
  reviewRequestAoLogsPerMinute: R.Number,
  semanticProjectVersionAoLogsPerMinute: R.Number,
  tableReferencesPerMinute: R.Number,
  drainOutputContentPagesPerRun: R.Number,
  drainOutputContentJobsPerRun: R.Number,
  drainOutputContentBatchSize: R.Number,
});
export type GarbageCollectionRateLimitConfig = R.Static<
  typeof GarbageCollectionRateLimitConfig
>;

export const DEFAULT_GARBAGE_COLLECTION_RATE_LIMIT_CONFIG: GarbageCollectionRateLimitConfig =
  {
    appSessionAoLogsPerMinute: 50_000,
    appSessionEventAppSessions: 100,
    appSessionEventLogs: 50_000,
    dataAppFileAoLogsPerMinute: 10_000,
    executionEventsPerMinute: 50_000,
    hexAoLogsPerMinute: 10_000,
    hexVersionAoLogsPerMinute: 10_000,
    OrgGuideFilesAOLogsPerMinute: 10_000,
    outputContentsPerRun: 1_000,
    outputsGracePeriodMinutes: 10,
    outputsPerMinute: 10_000,
    projectStatesPerMinute: 10_000,
    queryCacheEntriesPerMinute: 10_000,
    reviewRequestAoLogsPerMinute: 10_000,
    semanticProjectVersionAoLogsPerMinute: 10_000,
    tableReferencesPerMinute: 10_000,
    drainOutputContentPagesPerRun: 1_000_000,
    drainOutputContentJobsPerRun: 100,
    drainOutputContentBatchSize: 100,
  };

export type StringArrayJson = {
  values: string[];
};

export type ElasticsearchReindexConfig = {
  initialIdOffset: {
    columns?: string;
    databases?: string;
    schemas?: string;
    tables?: string;
  };
};

export type ElasticsearchIndexReaderConfig = {
  datasourceSchemas: string;
  projects: string;
};
type PerIndexConfig = {
  backfillIndexes: string[];
  // deprecated - use ElasticsearchIndexReaderConfig
  readerIndex: string;
  writerIndexes: string[];
};
export type ElasticsearchIndexConfig = {
  datasourceSchemas: PerIndexConfig;
  projects: PerIndexConfig;
};
export const HexToESSyncMode = {
  ALWAYS_COMMIT: "always_commit",
  COMMIT_ONLY: "commit_only",
  DISABLED: "disabled",
  ENABLED: "enabled",
};
export type HexToESSyncMode =
  (typeof HexToESSyncMode)[keyof typeof HexToESSyncMode];
export type QueryConfig = {
  category_and_status_boost?: number;
  creator: {
    weight: number;
  };
  description: {
    boost?: number | null;
  };
  last_updated_ts: {
    decay: number;
    offset: string;
    scale: string;
  };
  title: {
    boost?: number | null;
    // Whole query === whole title
    exactMatchBoost?: number | null;
    // title.startsWith(whole query)
    prefixMatchBoost?: number | null;
  };
};
export const DEFAULT_QUERY_CONFIG: QueryConfig = {
  category_and_status_boost: 1.2,
  creator: {
    weight: 2,
  },
  description: {
    boost: null,
  },
  last_updated_ts: {
    decay: 0.2,
    offset: "14d",
    scale: "90d",
  },
  title: {
    boost: null,
    exactMatchBoost: null,
    prefixMatchBoost: null,
  },
};

export type ComputeHeartbeatSyncToStripeStatus =
  | "JOB_DISABLED" // this queue will not run
  | "PROCESS_BUT_DONT_SEND" // the queue will run to mark compute heartbeats as seen, but none will be sent to stripe
  | "SEND_TO_STRIPE"; // the queue will run and send appropriate compute heartbeats to stripe

export const FastForwardOptions = R.Record({
  bufferSeconds: R.Number,
  debounce: R.Boolean,
  enableRequestCaching: R.Boolean,
  logging: R.Boolean,
  throttleTimeoutMs: R.Number,
});
export type FastForwardOptions = R.Static<typeof FastForwardOptions>;
export const DEFAULT_FAST_FORWARD_IMPLEMENTATION: FastForwardOptions = {
  bufferSeconds: 120,
  debounce: false,
  enableRequestCaching: false,
  logging: false,
  throttleTimeoutMs: 30_000,
};

export const RawFastForwardOptions = R.Partial({
  bufferSeconds: R.Number,
  debounce: R.Boolean,
  enableRequestCaching: R.Boolean,
  logging: R.Boolean,
  throttleTimeoutMs: R.Number,
});
export const RawFastForwardImplementationConfig = R.Partial({
  appSession: RawFastForwardOptions,
  hex: RawFastForwardOptions,
  hexVersion: RawFastForwardOptions,
  orgGuideFiles: RawFastForwardOptions,
  reviewRequest: RawFastForwardOptions,
  semanticProjectVersion: RawFastForwardOptions,
});
export type RawFastForwardImplementationConfig = R.Static<
  typeof RawFastForwardImplementationConfig
>;

/**
 * The config for the internal test collection queue that we use to run headless notebooks that are
 * part of special collections on a scheduled cadence.
 */
export type InternalTestCollectionQueue = {
  /**
   * The collection ids we want to run tests on.
   */
  collection_ids: string[];
  /**
   * Whether or not the internal test queue is enabled; including here so that we can disable outside of server restarts.
   */
  enabled: boolean;
  /**
   * How long each project run should have before we declare the run to take too long and kill it manually.
   */
  single_run_timeout_ms: number;
  /**
   * Settings for slack notifications based on queue success and failure.
   */
  slack_settings: {
    /**
     * The ID of the default slack channel to report success and failure messages to.
     * This can be overridden based on collection_id by using the overrides field.
     */
    default_slack_channel_id: string;
    /**
     * Overrides Slack behavior for a particular collectionId.
     */
    overrides: {
      [collectionId: string]: {
        run_as_user_id?: string;
        send_msg_on_success: boolean;
        slack_channel_id: string;
        tag_support_on_failure: boolean;
      };
    };
    /**
     * Whether or not we should send a slack message if the collection succeeds.
     */
    send_slack_on_success: boolean;
  };
};
export const DEFAULT_INTERNAL_TEST_COLLECTION_QUEUE = {
  collection_ids: [],
  cron: "* 0 0 0 0",
  enabled: false,
  single_run_timeout_ms: 120_000,
  slack_settings: {
    default_slack_channel_id: "",
    overrides: {},
    send_slack_on_success: false,
  },
};

export const DEFAULT_RUN_PROJECT_API_RATE_LIMIT_CONFIG: ApiRateLimitConfig = {
  hourRateLimit: 60,
  minuteRateLimit: 20,
};

export const DEFAULT_BASE_API_RATE_LIMIT_CONFIG: ApiRateLimitConfig = {
  hourRateLimit: 1800,
  minuteRateLimit: 30,
};

export const GeneralApiRateLimitConfig = R.Record({
  minuteRateLimit: R.Number,
});

export type GeneralApiRateLimitConfig = R.Static<
  typeof GeneralApiRateLimitConfig
>;

export const DEFAULT_GENERAL_API_RATE_LIMIT_CONFIG: GeneralApiRateLimitConfig =
  {
    minuteRateLimit: 60,
  };

export interface ClientDevtoolsFlagConfig {
  app_session_diff_checker: boolean;
  cell_debugger: boolean;
  chart_debugger: boolean;
  explore_debugger: boolean;
  external_profiler_user: boolean;
  hisc_debugger: boolean;
  internal_devtools_enabled: boolean;
  magic_debugger: boolean;
  multiplayer_debugger: boolean;
  python_error_reader: boolean;
  something_else: boolean;
  threads_debugger: boolean;
}

const DEFAULT_CLIENT_DEVTOOLS_FLAG_CONFIG: ClientDevtoolsFlagConfig = {
  app_session_diff_checker: false,
  cell_debugger: false,
  chart_debugger: false,
  explore_debugger: false,
  external_profiler_user: false,
  hisc_debugger: false,
  internal_devtools_enabled: false,
  magic_debugger: false,
  multiplayer_debugger: false,
  python_error_reader: false,
  something_else: false,
  threads_debugger: false,
};

export type BackfillQueryConfig = {
  queries: {
    batch_count: string;
    table: string;
  }[];
};

/** Per-event DELETE/UPDATE … LIMIT sizes for data source shard cleanup. */
export type DataSourceShardDeletionWorkBatchSizes = {
  categoryDeleteBatchSize: number;
  dataConnectionDeleteBatchSize: number;
  statusUpdateBatchSize: number;
};

export type DataSourceShardDeletionQueueConfig = {
  batchSizes: DataSourceShardDeletionWorkBatchSizes;
  cron: string;
  /** Max queue rows to load per job run. 0 skips the job. */
  peekBatchSize: number;
};

export const DEFAULT_BACKFILL_CONFIG = {
  queries: [],
};

export type WebsocketAuthCachingConfig = {
  timeout: number | null;
};

export type SemanticCapsPerEntityConfig = {
  [Entity in PermissionedEntityTypeName | "ENABLE_ALL_ENTITIES"]?: boolean;
};

// This LD flag represents whether or not we should use SemanticCaps auth checks for the entity type.
// The default config is an empty dictionary, meaning all SemanticCaps are disabled for each entity.
export const DEFAULT_SEMANTIC_CAPS_PER_ENTITY_CONFIG: SemanticCapsPerEntityConfig =
  {};

export type DataSourceSchemaSleeperConfig = {
  sleepEvery: number;
  sleepForMs: number;
};

export type DwhTableSearchConfig = {
  fts: {
    keywordBoost: number;
  };
  rerank: {
    // Roll out flag for the endorsed only query
    enableEndorsedSemanticGroup: boolean;
    rrf: {
      c: number;
    };
    weights: {
      endorsedBoost: number;
      endorsedSemantic: number;
      fts: number;
      semantic: number;
    };
  };
};

export type AuditLogWorkOSBackfillConfig = {
  config?: {
    dry_run: boolean;
    end_time: number;
    event_type_filter?: string[];
    orgIds?: string[];
    start_time: number;
  };
  enabled: boolean;
};

/**
 * One configured backfill plan for the backfill-indexing dispatcher. The
 * dispatcher runs hourly, filters to entries with `enabled: true`, and
 * starts a runner workflow per entry. `backfillName` is baked into the
 * runner's workflow ID, so leaving an entry enabled past one run is safe
 * — a duplicate start is rejected by Temporal until the operator bumps
 * `backfillName`.
 *
 * Each entry pairs a `table` (which selects the registered scanner) with
 * one or more `workflows` (the workflow types to fan out to per page).
 * The scanner must declare an adapter for each requested workflow type
 * or that entry will be skipped with an error log.
 */
export type BackfillIndexingPlanConfig = {
  /**
   * Operator-chosen identifier baked into the runner workflow ID. Bump
   * this to explicitly authorize re-running the same plan from cursor=null.
   */
  backfillName: string;
  enabled: boolean;
  /** Optional org scope. Omit to scan the entire table. */
  orgId?: OrgId;
  /**
   * Postgres table to scan. Must match a scanner registered in
   * `packages/server/src/temporal/backfillIndexing/plans`.
   */
  table: string;
};

export type BackfillIndexingPlansConfig = BackfillIndexingPlanConfig[];

export type SchemaRefreshRateLimitConfig = {
  enabled: boolean;
  globalLimit: number;
  manualGlobalLimitBoost?: number;
  manualOrgLimitBoost?: number;
  orgLimit: number;
};

export type SchemaRefreshInMemoryRateLimitConfig = RateLimitConfig & {
  batchSize: number;
};

const DEFAULT_SCHEMA_REFRESH_RATE_LIMIT_CONFIG: SchemaRefreshRateLimitConfig = {
  enabled: false,
  globalLimit: 10,
  manualGlobalLimitBoost: 0,
  manualOrgLimitBoost: 0,
  orgLimit: 1,
};

export type ReserveKernelsForInteractiveConfig = {
  maxReservations: number;
  poolReservationRatio: number;
};

export const DEFAULT_RESERVE_KERNELS_FOR_INTERACTIVE_CONFIG: ReserveKernelsForInteractiveConfig =
  {
    maxReservations: 30,
    poolReservationRatio: 0.2,
  };

export const CustomGraphqlObjectAuthzCheckType = {
  LOG_ONLY: "log_only",
  REQUIRE: "require",
  SKIP: "skip",
};
export type CustomGraphqlObjectAuthzCheckType =
  (typeof CustomGraphqlObjectAuthzCheckType)[keyof typeof CustomGraphqlObjectAuthzCheckType];

export type DisableSingleGraphqlObjectAuthz = {
  [key: string]: "DISABLE";
};

export type DisableProjectFiltersByType = {
  [key: string]: "DISABLE" | "ENABLE";
};

export type SchemaRefreshInterruptType =
  | "NO_INTERRUPT"
  | "PAUSE"
  | "DISRUPTIVE_CANCEL";

// Rollout ladder for the Snowflake delta schema refresh. off: full refresh.
// shadow: delta walk in verify mode (fetch all columns, attach markers, cross-check,
// no skip). on: delta walk that skips columns for unchanged tables.
export type SchemaRefreshDeltaMode = "off" | "shadow" | "on";

export type SchemaRefreshOrphanIdentificationThreshold = {
  entities: number;
  parents: number;
};

export const IdleKernelExpirySettings = R.Record({
  cronSchedule: R.Union(R.String, R.Undefined),
  gracePeriodMs: R.Union(R.Number, R.Undefined),
  limit: R.Union(R.Number, R.Undefined),
});

export type IdleKernelExpirySettings = R.Static<
  typeof IdleKernelExpirySettings
>;

export const defaultIdleKernelExpirySettings = {
  cronSchedule: "*/1 * * * *",
  gracePeriodMs: 30 * 1000,
  limit: 100,
} satisfies IdleKernelExpirySettings;

export const LanceSyncQueueLimiterSettings = R.Record({
  duration: R.Number,
  max: R.Number,
});

export type LanceSyncQueueLimiterSettings = R.Static<
  typeof LanceSyncQueueLimiterSettings
>;

export const defaultLanceSyncQueueLimiterSettings = {
  duration: 10_000, // Lance timeout is 10 seconds
  max: 10, // Our minimum number of pods is 10
} satisfies LanceSyncQueueLimiterSettings;

export const McpGetThreadToolConfig = R.Record({
  maxThreadStatusChecks: R.Number,
  threadStatusCheckIntervalMs: R.Number,
});
export type McpGetThreadToolConfig = R.Static<typeof McpGetThreadToolConfig>;

export interface PublicApiBanConfig {
  isBanned: boolean;
}

export const SemanticIngestEsRateLimitConfig = RateLimitConfig;
export type SemanticIngestEsRateLimitConfig = RateLimitConfig;

export const ApiRateLimitConfig = R.Record({
  hourRateLimit: R.Number,
  minuteRateLimit: R.Number,
});

export type ApiRateLimitConfig = R.Static<typeof ApiRateLimitConfig>;

export const AgentChatGarbageCollectorSettings = R.Record({
  agentChatThreadLimit: R.Number,
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  llmRequestLogLimit: R.Number,
});

export type AgentChatGarbageCollectorSettings = R.Static<
  typeof AgentChatGarbageCollectorSettings
>;

export const defaultAgentChatGarbageCollectorSettings: AgentChatGarbageCollectorSettings =
  {
    agentChatThreadLimit: 10_000,
    cron: "45 */4 * * *", // every 4 hours at 45 minutes past the hour
    disabled: false,
    llmRequestLogLimit: 50_000,
  };

export const AppSessionGarbageCollectorSettings = R.Record({
  cellLimit: R.Number,
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  limit: R.Number,
  outputLimit: R.Number,
});

export type AppSessionGarbageCollectorSettings = R.Static<
  typeof AppSessionGarbageCollectorSettings
>;

export const defaultAppSessionGarbageCollectorSettings: AppSessionGarbageCollectorSettings =
  {
    cellLimit: 10_000,
    cron: "*/15 * * * *", // every 15 minutes
    disabled: false,
    limit: 1_000,
    outputLimit: 10_000,
  };

export const ComputeActionGarbageCollectorSettings = R.Record({
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  limit: R.Number,
  maxAgeInSeconds: R.Number,
});

export type ComputeActionGarbageCollectorSettings = R.Static<
  typeof ComputeActionGarbageCollectorSettings
>;

export const defaultComputeActionGarbageCollectorSettings: ComputeActionGarbageCollectorSettings =
  {
    // Every 5 minutes starting at 3 minutes past the hour
    cron: "3-59/5 * * * *",
    // We require this flagged on in LD
    disabled: false,
    // 5000 every 5 minutes is roughly the rate prod produces events in Jan 2025
    limit: 5000,
    // Our current DataDog log retention is 3 days so hold onto them for 3 days is reasonable
    maxAgeInSeconds: 7 * 24 * 60 * 60,
  };

export const OrphanedFileDeletionJobSettings = R.Record({
  createdCutoff: StringDate,
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  iterations: R.Number,
});
export type OrphanedFileDeletionJobSettings = R.Static<
  typeof OrphanedFileDeletionJobSettings
>;

export const defaultOrphanedFileDeletionJobSettings: OrphanedFileDeletionJobSettings =
  {
    createdCutoff: StringDate.check("2025-09-19T00:00:00.000Z"), // only consider objects created before this cutoff
    cron: "*/10 * * * *", // every 10 minutes
    disabled: false,
    iterations: 10, // number of pages of files to check per job (page size limited to 1000 by S3)
  };

export const AsyncHiscConfig = R.Record({
  // Maximum number of async HISC cells permitted to be executing concurrently
  // in the sidecar's `ConcurrentQueueingJupyterExecutor`. Sync code cells are
  // blocked while any HISC cell in this group is running. A missing or
  // non-positive value falls back to `DEFAULT_HISC_CONCURRENT_CELL_LIMIT`; a
  // non-positive limit would otherwise wedge the queue (nothing dequeues).
  concurrentCellLimit: R.Number.optional(),
  enableBetweenCells: R.Boolean.optional(),
  enableWithinCell: R.Boolean.optional(),
  parallelInstructionLimit: R.Number.optional(),
  // When non-null, replaces the `parallelizable=True` flags in the Python
  // source. Elements should be HISC instruction `kind` strings
  // (see `HiscInstructionKind`). Unknown kinds are ignored on the Python side.
  parallelizableInstructions: R.Array(R.String).optional(),
});
export type AsyncHiscConfig = R.Static<typeof AsyncHiscConfig>;

export const MagicNotebookLogicViewConfig = R.Record({
  enabled: R.Boolean,
  includeExperimentalTools: R.Boolean.optional(),
  maxAgentIterations: R.Number,
});
export type MagicNotebookLogicViewConfig = R.Static<
  typeof MagicNotebookLogicViewConfig
>;
export const defaultMagicNotebookLogicViewConfig: MagicNotebookLogicViewConfig =
  {
    enabled: false,
    maxAgentIterations: 0,
  };

export const TemporalWorkerConcurrencyConfig = R.Record({
  maxConcurrentActivityTaskExecutions: R.Number,
  maxConcurrentWorkflowTaskExecutions: R.Number,
});
export type TemporalWorkerConcurrencyConfig = R.Static<
  typeof TemporalWorkerConcurrencyConfig
>;
export const defaultTemporalWorkerConcurrencyConfig: TemporalWorkerConcurrencyConfig =
  {
    maxConcurrentActivityTaskExecutions: 75,
    maxConcurrentWorkflowTaskExecutions: 50,
  };

export const TemporalEvalWorkerConcurrencyConfig = R.Record({
  maxConcurrentActivityTaskExecutions: R.Number,
  maxConcurrentWorkflowTaskExecutions: R.Number,
});
export type TemporalEvalWorkerConcurrencyConfig = R.Static<
  typeof TemporalEvalWorkerConcurrencyConfig
>;
export const defaultTemporalEvalWorkerConcurrencyConfig: TemporalEvalWorkerConcurrencyConfig =
  {
    maxConcurrentActivityTaskExecutions: 30,
    maxConcurrentWorkflowTaskExecutions: 10,
  };

export const TemporalBackgroundReviewWorkerConcurrencyConfig = R.Record({
  maxConcurrentActivityTaskExecutions: R.Number,
  maxConcurrentWorkflowTaskExecutions: R.Number,
});
export type TemporalBackgroundReviewWorkerConcurrencyConfig = R.Static<
  typeof TemporalBackgroundReviewWorkerConcurrencyConfig
>;
export const defaultTemporalBackgroundReviewWorkerConcurrencyConfig: TemporalBackgroundReviewWorkerConcurrencyConfig =
  {
    maxConcurrentActivityTaskExecutions: 75,
    maxConcurrentWorkflowTaskExecutions: 50,
  };

export const TemporalHeroWorkerConcurrencyConfig = R.Record({
  maxConcurrentActivityTaskExecutions: R.Number,
  maxConcurrentWorkflowTaskExecutions: R.Number,
});
export type TemporalHeroWorkerConcurrencyConfig = R.Static<
  typeof TemporalHeroWorkerConcurrencyConfig
>;
/**
 * Backstop defaults. The primary concurrency knob is the Hero
 * pipeline's config (`hero-pipeline-config`): it holds a
 * fixed number of permits (`maxConcurrentAnalyzers` / `maxConcurrentAggregators`)
 * and only dispatches a new analyzer/aggregator run when an in-flight one
 * finishes and frees a permit, keeping at most that many runs in flight at once.
 */
export const defaultTemporalHeroWorkerConcurrencyConfig: TemporalHeroWorkerConcurrencyConfig =
  {
    maxConcurrentActivityTaskExecutions: 30,
    maxConcurrentWorkflowTaskExecutions: 10,
  };

export type TypeaheadV2Config = {
  enabledPython: boolean;
  enabledSql: boolean;
  modelKeys: { dev: string; eu: string; us: string };
};

export const HybridIndexConfig = R.Record({
  // Controls the baseten model used for computing dense vector embeddings.
  denseEmbeddingModelKey: R.String,
});
export type HybridIndexConfig = R.Static<typeof HybridIndexConfig>;
export const defaultHybridIndexConfig: HybridIndexConfig = {
  denseEmbeddingModelKey: "UNSET_MODEL_KEY",
};

export const StripOutputContentsConfig = R.Record({
  debounceTimeoutMs: R.Number,
  inlineCutoffBytes: R.Number,
  maxWaitMs: R.Number,
  stripContents: R.Boolean,
});
export type StripOutputContentsConfig = R.Static<
  typeof StripOutputContentsConfig
>;
export const DEFAULT_STRIP_OUTPUT_CONTENTS_CONFIG: StripOutputContentsConfig = {
  debounceTimeoutMs: 500,
  inlineCutoffBytes: CONSIDER_LAZY_LOADING_SIZE,
  maxWaitMs: 2000,
  stripContents: true,
};

export const AppSessionEventBatchingConfig = R.Record({
  debounceTimeout: R.Number,
  maxBatchSize: R.Number,
  maxWait: R.Number,
});
export type AppSessionEventBatchingConfig = R.Static<
  typeof AppSessionEventBatchingConfig
>;
export const DEFAULT_APP_SESSION_BATCHING_CONFIG: AppSessionEventBatchingConfig =
  {
    debounceTimeout: 10,
    maxBatchSize: 25,
    maxWait: 100,
  };

export const AppSessionEventsConfig = R.Partial({
  apiSessions: R.Boolean,
  defaultSessions: R.Boolean,
  logicSessions: R.Boolean,
  publishedSessions: R.Boolean,
  scheduledSessions: R.Boolean,
});
export type AppSessionEventsConfig = R.Static<typeof AppSessionEventsConfig>;
export const DEFAULT_APP_SESSION_EVENTS_CONFIG: Required<AppSessionEventsConfig> =
  {
    apiSessions: true,
    defaultSessions: true,
    logicSessions: false,
    publishedSessions: true,
    scheduledSessions: true,
  };

export const EmbeddingModelProviderConfig = R.Record({
  batchSize: R.Number,
  concurrency: R.Number,
});
export type EmbeddingModelProviderConfig = R.Static<
  typeof EmbeddingModelProviderConfig
>;

export const HexVersionAutosaveGarbageCollectorSettings = R.Record({
  appSessionBatchSize: R.Number,
  batchSize: R.Number,
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  limit: R.Number,
  shouldLogAutosaveIds: R.Boolean,
});

export type HexVersionAutosaveGarbageCollectorSettings = R.Static<
  typeof HexVersionAutosaveGarbageCollectorSettings
>;

export const defaultHexVersionAutosaveGarbageCollectorSettings: HexVersionAutosaveGarbageCollectorSettings =
  {
    appSessionBatchSize: 100,
    batchSize: 20,
    cron: "10 * * * *",
    disabled: false,
    limit: 100,
    shouldLogAutosaveIds: false,
  };

export type TopicWorkflowConfig = R.Static<typeof TopicWorkflowConfig>;

export type SuggestionSourceGroupingConfig = R.Static<
  typeof SuggestionSourceGroupingConfig
>;

export const defaultSuggestionSourceGroupingConfig: SuggestionSourceGroupingConfig =
  {
    backgroundReviewEnabled: false,
    backgroundReviewIncludeNoOpIssues: false,
    backgroundReviewMaxIssuesPerRun: 20,
    backgroundReviewRerunAfterDays: 7,
    maxConcurrentOrgs: 1,
    maxIssuesPerOrg: 100,
    maxOrgsPerRun: 50,
    maxSources: 100,
    minSourcesForEligibility: 15,
    minSourcesPerIssue: 3,
    minThreadsPerIssue: 2,
    minUsersPerIssue: 1,
    rejectedIssuesLookbackLimit: 20,
  };

export const SuggestionSourceGroupingConfig = R.Record({
  backgroundReviewEnabled: R.Boolean,
  backgroundReviewIncludeNoOpIssues: R.Boolean,
  backgroundReviewMaxIssuesPerRun: R.Number,
  backgroundReviewRerunAfterDays: R.Number,
  maxConcurrentOrgs: R.Number,
  maxIssuesPerOrg: R.Number,
  maxOrgsPerRun: R.Number,
  maxSources: R.Number,
  minSourcesForEligibility: R.Number,
  minSourcesPerIssue: R.Number,
  minThreadsPerIssue: R.Number,
  minUsersPerIssue: R.Number,
  rejectedIssuesLookbackLimit: R.Number,
});

export const defaultTopicWorkflowConfig: TopicWorkflowConfig = {
  classificationBatchSize: 25,
  delayBetweenOrgsMs: 60000,
  maxBootstrapTopics: 50,
  maxConcurrentOrgs: 1,
  maxIncrementalTopics: 10,
  maxOrgsPerDiscoveryRun: 100,
  minNewThreadsForIncrementalDiscovery: 10,
  minThreadsForInitialDiscovery: 10,
  minThreadsPerTopic: 3,
  minUsersPerTopic: 1,
  threadLimitForDiscovery: 500,
};

export const TopicWorkflowConfig = R.Record({
  // Max number of thread summaries to classify in one batch
  classificationBatchSize: R.Number,
  // How long to wait between running discovery for orgs
  delayBetweenOrgsMs: R.Number,
  // Max number of initial topics to discover in org's first discovery run
  maxBootstrapTopics: R.Number,
  // How many orgs can have discovery workflows running concurrently
  maxConcurrentOrgs: R.Number,
  // Max number of incremental topics to discover in one incremental discovery run
  maxIncrementalTopics: R.Number,
  // Max number of orgs to process in one discovery workflow run
  maxOrgsPerDiscoveryRun: R.Number,
  // Min number of new threads since last discovery required to run incremental discovery
  minNewThreadsForIncrementalDiscovery: R.Number,
  // Min number of thread summaries for initial discovery
  minThreadsForInitialDiscovery: R.Number,
  // Min number of threads required for a topic to be considered valid
  // (This is an OR condition with minUsersPerTopic, so at least one of the
  // two must be met)
  minThreadsPerTopic: R.Number,
  // Min number of users required for a topic to be considered valid
  // (This is an OR condition with minThreadsPerTopic, so at least one of the
  // two must be met)
  minUsersPerTopic: R.Number,
  // Max number of thread summaries to load for discovery
  threadLimitForDiscovery: R.Number,
});

export const SupportUserSharingCleanupSettings = R.Record({
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  limit: R.Number,
});

export type SupportUserSharingCleanupSettings = R.Static<
  typeof SupportUserSharingCleanupSettings
>;

export const defaultSupportUserSharingCleanupSettings: SupportUserSharingCleanupSettings =
  {
    cron: "30 3 * * *", // Daily at 3:30 AM UTC
    disabled: false,
    limit: 100,
  };

export const UsercheckSignal = R.Union(
  R.Literal("blocklisted"),
  R.Literal("disposable"),
  R.Literal("recent_domain"),
  R.Literal("relay_domain"),
  R.Literal("spam"),
);
export type UsercheckSignal = R.Static<typeof UsercheckSignal>;

export const UsercheckDomainDenialConfig = R.Record({
  alertOnlySignals: R.Array(UsercheckSignal),
  blockedSignals: R.Array(UsercheckSignal),
  recentDomainAgeThresholdDays: R.Number,
  // Example: ["(^|\\.)trusted\\.com$"] matches trusted.com and its subdomains.
  whitelistedDomains: R.Array(R.String),
});
export type UsercheckDomainDenialConfig = R.Static<
  typeof UsercheckDomainDenialConfig
>;

export const defaultUsercheckDomainDenialConfig: UsercheckDomainDenialConfig = {
  alertOnlySignals: [],
  blockedSignals: [],
  recentDomainAgeThresholdDays: 30,
  whitelistedDomains: [],
};

export const OrgCreationDomainRateLimitConfig = z.object({
  // Master on/off switch for the per-domain org-creation rate limit.
  enabled: z.boolean(),
  // When false, exceeding the limit only logs + emits a metric (alert-only);
  // when true, org creation is rejected once the limit is exceeded.
  enforce: z.boolean(),
  // Default max orgs a single business domain may create within the window.
  maxOrgsPerWindow: z.number(),
  // Rolling window length in seconds (RateLimiterRedis `duration`).
  windowSeconds: z.number(),
  // Per-domain overrides (lowercased domain -> max orgs per window), e.g. for
  // educational domains where many people each legitimately want their own org.
  domainOverrides: z.record(z.string(), z.number()),
});
export type OrgCreationDomainRateLimitConfig = z.infer<
  typeof OrgCreationDomainRateLimitConfig
>;

export const defaultOrgCreationDomainRateLimitConfig: OrgCreationDomainRateLimitConfig =
  {
    enabled: false,
    enforce: false,
    maxOrgsPerWindow: 3,
    windowSeconds: 86_400,
    domainOverrides: {},
  };

export const TaskExpirationSettings = R.Record({
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  limit: R.Number,
});

export type TaskExpirationSettings = R.Static<typeof TaskExpirationSettings>;

export const defaultTaskExpirationSettings: TaskExpirationSettings = {
  cron: "55 * * * *", // Every hour at :55
  disabled: false,
  limit: 1_000,
};

export const TaskCleanupSettings = R.Record({
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  limit: R.Number,
});

export type TaskCleanupSettings = R.Static<typeof TaskCleanupSettings>;

export const defaultTaskCleanupSettings: TaskCleanupSettings = {
  cron: "10 4 * * *", // Daily at 4:10 AM UTC
  disabled: false,
  limit: 1_000,
};

export const AiBillingEnforcementActivationOrgsConfig = R.Record({
  maxOrgsPerRun: R.Number,
  mode: R.Union(
    R.Literal("ORG_IDS"),
    R.Literal("DUE_ENFORCEMENT_DATE"),
  ).optional(),
  orgIds: R.Array(OrgId),
});
export type AiBillingEnforcementActivationOrgsConfig = R.Static<
  typeof AiBillingEnforcementActivationOrgsConfig
>;

export const AiBillingCostAlertThresholdsConfig = R.Record({
  /** Warn once a thread's cumulative spend crosses this many credits. */
  threadCostWarnCredits: R.Number,
  /**
   * Warn when a single LLM call (billing row) costs at least this multiple of
   * the immediately preceding call's cost in the same run.
   */
  iterationCostSpikeMultiplier: R.Number.optional(),
  /**
   * Floor (in credits) for the relative spike alert: a call must cost at least
   * this much before it can be flagged as a spike, so tiny call-to-call jumps
   * (e.g. 0.01 → 0.05 credits) don't generate noise.
   */
  iterationCostSpikeMinCredits: R.Number.optional(),
});
export type AiBillingCostAlertThresholdsConfig = R.Static<
  typeof AiBillingCostAlertThresholdsConfig
>;
export const defaultAiBillingCostAlertThresholds: AiBillingCostAlertThresholdsConfig =
  {
    threadCostWarnCredits: 40,
    iterationCostSpikeMultiplier: 4,
    iterationCostSpikeMinCredits: 1,
  };

export interface FeatureFlagSet extends FlaggedNewUserFeatureSet {
  "additional-mariadb-params": boolean;
  /** Shows the org-admin "Default model" setting row (Settings > AI & Agents > Model settings). */
  "admin-specify-default-model": boolean;
  "agent-auto-data-connection": boolean;
  "agent-chat-garbage-collector-settings": unknown; // expected to be Partial<AgentChatGarbageCollectorSettings>
  "agent-data-app-console-log-strategy": DataAppConsoleLogStrategy;
  "agent-data-app-screenshot-strategy": DataAppScreenshotStrategy;
  "agent-desk": boolean;
  /**
   * Gen-apps agent writes files via the agent desk sandbox (ShellExec); they
   * are synced back automatically (before captures and at the end of the
   * run) instead of going through the VFS file write tools. Only has effect
   * when `agent-desk` is also enabled.
   */
  "agent-desk-gen-app-writes": boolean;
  "agent-desktop-notifications": boolean;
  "agent-eager-input-streaming": boolean;
  "agent-fast-mode": boolean;
  /**
   * Gives the agent a tool to attach an inline, clickable download link to a
   * file it created (or that the user uploaded) so the user can download it
   * directly from the agent's message.
   */
  "agent-file-download-links": boolean;
  "agent-knowledge-search-tool": boolean;
  "agent-llm-retry-config": AgentLlmRetryConfig;
  "agent-main-chart-tool": boolean;
  "agent-model-effort-picker": boolean;
  "agent-plain-code-project-capture-strategy": PlainCodeProjectCaptureStrategy;
  "agent-pre-workflow-setup": boolean;
  "agent-prompt-cache-routing": AgentPromptCacheRoutingConfig;
  "agent-run-scopes": boolean;
  "agent-run-sql-default-timeout-seconds": number;
  /**
   * Gates the acting-user Slack channel membership check for the
   * fetch-Slack-thread-messages tool.
   */
  "agent-slack-thread-context-channel-access-check": boolean;
  "agent-slack-thread-context-tool": boolean;
  /**
   * Gates verifying that a Slack channel is public (via conversations.info)
   * before org-sharing a Slack-created Ask thread. When enabled, threads
   * created from private channels are instead shared individually with the
   * channel's current members (mapped to Hex users) rather than the whole
   * org. Off by default for gradual rollout.
   */
  "agent-slackbot-public-channel-share-check": boolean;
  /**
   * Controls the single "continuing this thread may be expensive" warning shown
   * on stale, long threads instead of the legacy "this conversation is getting
   * long" banner. The `enabled` field gates the banner; the remaining fields let
   * us tune the copy and thresholds without a code change. Malformed values fall
   * back per-field to {@link DEFAULT_AGENT_STALE_THREAD_WARNING_CONFIG}.
   */
  "agent-stale-thread-warning-config": AgentStaleThreadWarningConfig;
  "agent-stop-cell-tool": boolean;
  "agent-tasks": boolean;
  "agent-tasks-cleanup-job-settings": unknown; // expected to be Partial<TaskCleanupSettings>
  "agent-tasks-create-tool": boolean;
  "agent-tasks-expiration-job-settings": unknown; // expected to be Partial<TaskExpirationSettings>
  "agent-thread-artifacts": boolean;
  "agent-thread-artifacts-frontend": boolean;
  "agent-thread-model-stamping": boolean;
  "agent-thread-viewport-fill-pagination": boolean;
  "agent-view-tables-column-regex": boolean;
  "agent-wait-for-cell-config": AgentWaitForCellConfig;
  /** Dollar thresholds that drive anomalous agent-cost log alerts. */
  "ai-billing-cost-alert-thresholds": AiBillingCostAlertThresholdsConfig;
  "ai-billing-enforcement-activation-orgs": AiBillingEnforcementActivationOrgsConfig;
  /**
   * JSON record keyed by FeatureExemptBillingReason with boolean values.
   * true = exempt (not billed), false = not exempt (billed).
   * Missing keys default to exempt, unexpected keys are ignored.
   */
  "ai-billing-feature-exemption": Partial<
    Record<FeatureExemptBillingReasonType, boolean>
  >;
  "ai-billing-internal-orgs": boolean;
  "ai-credit-enforcement": boolean;
  "ai-credit-topup-consolidate-invoice-items": boolean;
  /**
   * When enabled for a self-serve org, synchronously attempt payment on the
   * one-time AI credit top-up invoice right after finalizing it, instead of
   * waiting for Stripe's asynchronous auto-collection.
   */
  "ai-credit-topup-immediate-charge": boolean;
  "allow-data-retention-models": boolean;
  "allow-duplication-for-project": boolean;
  "allow-embedded-user": boolean;
  "allow-fable-model": boolean;
  "allow-public-domain-signups": boolean;
  "allow-r-project-creation": boolean;
  "allow-semantic-endorsed-filter": boolean;
  "ao-rate-limit": Record<string, unknown>;
  "app-builder-last-element-resize": boolean;
  "app-loading-bar-delay-seconds": number;
  "app-loading-bar-initial-percent": number;
  "app-run-button-memory": boolean;
  "app-run-button-progress": boolean;
  // experiment flag to be configured at 50% by app session
  "app-session-a-a": boolean;
  "app-session-event-batching-config": unknown;
  "app-session-events-config": unknown;
  "app-session-garbage-collector-settings": unknown; // expected to be Partial<AppSessionGarbageCollectorSettings>
  "app-session-mp-client-logging": boolean;
  "artifact-extraction-agent-intent-summary-tools": boolean;
  "ask-agent-slack-bot": boolean;
  "audit-log-llm-requests-enabled": boolean;
  "audit-log-workos-backfill": AuditLogWorkOSBackfillConfig;
  "audit-log-workos-backfill-interrupt": boolean;
  "auth-fanout-diagnostic-threshold": number;
  "auto-rebuild-custom-images-delay": number;
  "auto-rebuild-docker-images": boolean;
  "auto-refresh-client-config": Record<string, unknown>;
  "backfill-embedded-oauth-credentials": boolean;
  /**
   * Page size for backfill-indexing scans. Re-read by the scan activity
   * on every page, so operators can ramp page size up or down mid-run
   * without restarting. Keyset pagination handles size changes safely —
   * each page asks for `id > cursor LIMIT N` regardless of prior N.
   */
  "backfill-indexing-page-size": number;
  /**
   * Per-plan config for the backfill-indexing temporal dispatcher.
   * See `BackfillIndexingPlansConfig` for the payload shape.
   */
  "backfill-indexing-plans": BackfillIndexingPlansConfig;
  "base-kernel-image-gate": {
    defaultPythonKernelImage: string;
    enabled: string[];
    /**
     * Per-kernel-image deprecation schedule. SELECTION-only — controls
     * picker visibility and new-project default resolution. Existing
     * HexVersions keep running on whatever kernel image is pinned on
     * them, and null-kernel HexVersions still resolve via the legacy
     * `python39NullCutoffDate` path unchanged. Force-migrating existing
     * projects off a deprecated kernel is a separate mechanism
     * (milestone 3 / NOVA-3214) and must not be folded into these
     * selection cutoffs.
     *
     * Each entry can specify zero or more cutoff dates:
     *   - disableNewSelectionsForAll: once `now` is on/after this ISO
     *     date, nobody can select this kernel.
     *   - disableNewSelectionsForOrgsCreatedAfter: orgs whose `createdDate`
     *     is on/after this ISO date can't select this kernel.
     *
     * Adding a new kernel deprecation = adding a key to this map.
     */
    kernelImageDeprecations?: Record<
      string,
      {
        disableNewSelectionsForAll?: string;
        disableNewSelectionsForOrgsCreatedAfter?: string;
      }
    >;
    python39NullCutoffDate: string;
  };
  "baseten-embedding-model-key": string;
  "bedrock-custom-model-mapping": boolean;
  "bulk-deactivate-users-by-domain": boolean;
  "bull-queue-job-timeout-override": Record<string, unknown>; // expected Record<BullQueueId, number>
  "bull-queue-repair-maxed-groups": boolean;
  "bull-queue-slow-job-threshold-override": Record<string, unknown>;
  "byok-bedrock": boolean;
  "byok-enabled-override": boolean;
  /** Gemini Enterprise Agent Platform (formerly Vertex AI) */
  "byok-vertex": boolean;
  "cell-compilation-concurrency": number;
  "cell-compilation-merge-stages": boolean;
  "cell-compilation-timeout-ms": number;
  "cell-debugger": boolean;
  "cell-references-fix-migration": boolean;
  "chart-cheat-sheet": boolean;
  "chat-with-generative-app": boolean;
  "check-kernel-limits": boolean;
  "clickhouse-client-buffer-size": number;
  "clickhouse-json-column": boolean;
  "clickhouse-query-timeout-seconds": number;
  "client-devtools": ClientDevtoolsFlagConfig;
  "collaborative-project-threads": boolean;
  "compute-action-garbage-collector-settings": Partial<ComputeActionGarbageCollectorSettings>;
  "compute-heartbeat-send-to-segment-enabled": boolean;
  "compute-heartbeat-sync-to-stripe-cron-schedule": string;
  "compute-heartbeat-sync-to-stripe-mode": string;
  "compute-heartbeat-sync-to-stripe-status": ComputeHeartbeatSyncToStripeStatus;
  "connections-per-trace-debounce-ms": number;
  "connections-per-trace-warning-threshold": number;
  "context-studio-saved-views": boolean;
  "cp-diff-tracking-cron-schedule": string;
  "cp-diff-tracking-delay-threshold-ms": number;
  "cp-diff-tracking-enabled": boolean;
  "cp-kernel-management": KernelManagementCPMigrationState;
  "cryptominer-regex": string;
  "csv-download-standard-data-service-auth": boolean;
  "custom-kernels-new-auto-rebuild-logic": boolean;
  "custom-kernels-ui": boolean;
  "custom-kernels-ui-v2": boolean;
  "data-app-chart-csv-download": boolean;
  "data-app-file-ao-editing": boolean;
  "data-conn-diff-schema-update": boolean;
  "data-conn-diff-schema-update-threshold": number;
  "data-conn-sleep-times": DataSourceSchemaSleeperConfig;
  "data-connection-athena-sql-schema-discovery": boolean;
  "data-connection-clickhouse-chdb-allowed": boolean;
  "data-connection-databricks-proxy": boolean;
  "data-connection-deletion-job-limit": number;
  "data-connection-motherduck-enable-read-scaling": boolean;
  "data-connection-snowflake-async-queries": boolean;
  "data-connection-types-to-limit": Partial<
    Record<DataConnectionType | "ssh", number>
  >;
  "data-connections-header-cleanup": boolean;
  "data-recommended-tab": boolean;
  "data-retention-available": boolean;
  "data-service-dfs-batch-size": number;
  "data-service-dfs-max-bytes-per-batch": number;
  "data-service-skip-hex-row-index": boolean;
  "data-service-table-load-batch-size": number;
  "data-source-shard-deletion-queue-config": DataSourceShardDeletionQueueConfig;
  "database-backfill-cron": string;
  "database-backfill-queries": BackfillQueryConfig;
  "dbt-data-conn-sleep-times": DataSourceSchemaSleeperConfig;
  "dbt-disallow-proxy-server": boolean;
  "dbt-fetch-batch-size": number;
  "dbt-timeout-metadata": number;
  "dd-retain-execution-trace": boolean;
  "deadlock-detector-timeout-s": number;
  "debug-gh-sync": boolean;
  "default-api-rate-limit-config": ApiRateLimitConfig;
  "default-display-table": boolean;
  "delete-app-session-api-client-on-kill": boolean;
  /** When true, SQL-cell execution disables allowDuplicateColumns at source
   * generation, so the data-service dedupes column names instead of `_hex{n}_`
   * encoding them. Step 1 of retiring the legacy allowDuplicateColumns encoding. */
  "disable-allow-duplicate-columns": boolean;
  "disable-ao-garbage-collector": boolean;
  "disable-bull-queue-job-timeout": boolean;
  "disable-chat-support-for-company-closure": {
    available: boolean;
    cta: string;
    message: string;
  };
  "disable-context-changeset-garbage-collector": boolean;
  "disable-count-query-for-deletion-utils": boolean;
  "disable-dereference-agent-client-to-user": boolean;
  "disable-embedded-user-api-client-garbage-collector": boolean;
  "disable-execution-event-writes": boolean;
  "disable-hex-garbage-collector": boolean;
  "disable-hex-version-default-app-session-garbage-collector": boolean;
  "disable-legacy-execution-events-storage": boolean;
  "disable-presigned-embed-url-garbage-collector": boolean;
  "disable-safe-logging-for-agent-provider-errors": boolean;
  "disable-table-preview-api-client-garbage-collector": boolean;
  "disable-workspace-sharing": boolean;
  "do-not-charge-for-kernels-created-by-hex": boolean;
  "dwh-table-search-config": DwhTableSearchConfig;
  /** Direct chart editing in gen apps: edit mode, per-element selection, and the viz edit panel. */
  "editable-data-app-charts": boolean;
  "embedded-user-notification-can-use-custom-cron": boolean;
  "embedded-user-notification-limit": number;
  "embeddings-baseten-model-keys": { [K in BasetenEmbeddingModel]?: string };
  "embeddings-garbage-collection": boolean;
  "embeddings-garbage-collection-deletion-batch-size": number;
  "embeddings-garbage-collection-search-batch-size": number;
  "embeddings-mpk-config": {
    [keyProvider in LanceEmbeddingProvider]?: {
      [keyModel in LanceEmbeddingModel]?: EmbeddingModelProviderConfig;
    };
  };
  "embeddings-orphan-deletion": boolean;
  "embeddings-orphan-deletion-batch-size": number;
  "embeddings-orphan-search-minimum-batch-size": number;
  "embeddings-project-retrieval-mpk": EmbeddingModelProviderKeyType;
  "embeddings-table-retrieval-mpk": EmbeddingModelProviderKeyType;
  "emit-dbt-data-size-metric": boolean;
  "enable-agent-threads-indexing": boolean;
  "enable-auth-fanout-diagnostic": boolean;
  "enable-committed-credit-grant-sync": boolean;
  "enable-cross-connection-table-search": boolean;
  "enable-cross-request-semantic-cap-cache": boolean;
  "enable-cryptominer-regex": boolean;
  "enable-data-service-csv-sanitization": boolean;
  "enable-delete-pods-from-wiz-issues": boolean;
  "enable-embedding-app-freshness-options": boolean;
  // When enabled, run/trace execution-stats side effects (wall-clock duration +
  // stats export) fire from the legacy server-side IDLE/ERRORED dispatch. When
  // disabled (default), they are driven per-trace by the sidecar's
  // `recordRunComplete` mutation so each queued-up run is finalized.
  "enable-exec-events-sideeffects": boolean;
  "enable-execution-events-s3-archive": boolean;
  "enable-external-file-integration-kernel-api": boolean;
  "enable-hex-cli-endpoints": boolean;
  "enable-iam-role-based-ecr-repo-authentication": boolean;
  "enable-iam-role-based-s3-integration-authentication": boolean;
  "enable-kernel-write-parquet": boolean;
  "enable-monitor-replication-slots": boolean;
  "enable-oauth-schema-auth": boolean;
  "enable-open-in-agent-dev-tools": boolean;
  "enable-read-execution-events-from-s3": boolean;
  "enable-reindex-schema": boolean;
  "enable-rerank-search-users": boolean;
  "enable-run-project-with-trace": boolean;
  "enable-scheduled-run-retry": boolean;
  "enable-semantic-cap-entity-memo": boolean;
  "enable-snowflake-oauth-sso": boolean;
  "enable-sql-completion-logs": boolean;
  "enable-sync-field-auth-fast-path": boolean;
  "enable-workspace-thread-search": boolean;
  "enforce-compute-spend-limits": boolean;
  "es-bulk-delete-chunk-size": number;
  "es-bulk-timeout-schema-refresh": string;
  "es-delete-all-datasources-delay-ms": number;
  "es-delete-by-query-timeout": string;
  "es-index-config": ElasticsearchIndexConfig;
  "es-index-reader-config": ElasticsearchIndexReaderConfig;
  "es-reindex-config": ElasticsearchReindexConfig;
  "es-sync-batch-size": number;
  "es-sync-force-to-head-when-stuck": boolean;
  "es-sync-hex-chunk-size": number;
  "es-sync-hex-job-limit": number;
  "es-sync-max-batch-size": number;
  "experimentation-test": boolean;
  "explore-sankey": boolean;
  // If enabled, Explorer role is visible and can be assigned to users.
  // Deprecated: Use Org.explorerRoleEnabled instead.
  "explorer-role": boolean;
  "fast-forward-implementation": unknown;
  "flag-data-team-button": boolean;
  "flat-subagent-workflow": boolean;
  "force-live-updates-for-flags": FeatureFlag[] | ["*"];
  /** When true, HTML outputs use blob URLs instead of the outputs subdomain iframe. */
  "force-output-iframe-blob": boolean;
  "force-redefinition-edges": boolean;
  "garbage-collection-rate-limit-config": GarbageCollectionRateLimitConfig;
  "gen-apps-projects-as-context": boolean;
  "general-api-rate-limit-config": GeneralApiRateLimitConfig;
  // TODO we can coalesce grant-access-for-project-from-slack with slack-interactivity
  "grant-access-for-project-from-slack": boolean;
  "headless-notebook-agent-threads": boolean;
  "hero-frontend": boolean;
  "hero-pipeline-config": HeroPipelineConfig;
  "hex-garbage-collection-app-session-cell-limit": number;
  "hex-garbage-collection-hex-version-limit": number;
  "hex-to-es-sync-mode": HexToESSyncMode;
  "hex-version-autosave-gc-settings": unknown; // expected Partial<HexVersionAutosaveGarbageCollectorSettings>
  "hide-unsubscribe-link-for-sedgwick-in-embeds": boolean;
  "hisc-async-config": AsyncHiscConfig;
  "home-page-suggestions": boolean;
  "hourly-schedule-limit": number;
  "hybrid-index-config": HybridIndexConfig;
  "idle-kernel-expiry-settings": IdleKernelExpirySettings;
  "in-app-announcements": Record<string, unknown>[];
  "in-app-releases": Record<string, unknown>;
  "in-app-whats-new": Record<string, unknown>;
  "internal-test-collection-job-delay-ms": number;
  "internal-test-collection-queue": InternalTestCollectionQueue;
  "kernel-init-timeout-ms": number;
  "kernel-lifetime-stats": boolean;
  "kernel-metric-logger": boolean;
  "kernel-metric-logger-code": boolean;
  "knowledge-dev-tools": boolean;
  "lance_db-sync-limiter-settings": Record<string, unknown>;
  "lance-db-data-conn-backfill-data-conn-to-skip": string[];
  "lance-db-data-conn-backfill-frequency-ms": number;
  "lance-db-data-conn-backfill-limit": number;
  "lance-db-data-conn-backfill-retry-limit": number;
  "lance-db-data-conn-errored-actions-limit": number;
  "lance-db-data-conn-stuck-in-backfilling-limit": number;
  "lance-db-entity-promotion-limit": number;
  "lance-db-hex-backfill": boolean;
  "lance-db-hex-backfill-excluded-orgs": string[];
  "lance-db-hex-backfill-frequency-ms": number;
  "lance-db-hex-backfill-limit": number;
  "lance-db-hex-errored-actions-limit": number;
  "lance-db-hex-max-sync-attempts": number;
  "lance-db-hex-stuck-in-backfilling-limit": number;
  "lance-db-hex-stuck-in-processing-limit": number;
  "lance-db-long-running-action-threshold-hours": number;
  "lance-db-sync-batch-lance-actions-queue-interval-ms": number;
  "lance-db-sync-batch-size": number;
  "lance-db-table-retrieval-limit": number;
  "lance-db-table-retrieval-timeout-ms": number;
  "lance-db-un-indexed-rows-threshold": number;
  "lance-db-where-batch-size": number;
  "legacy-git-sync": boolean;
  "log-dbt-metadata-counts": boolean;
  "logic-session-timeout-extension": boolean;
  "magic-esignature": boolean;
  "magic-lance-project-retrieval-limit": number;
  "magic-lance-project-retrieval-vector-distance-threshold": number;
  "magic-max-other-dfs": number;
  "magic-notebook-logic-view-config": MagicNotebookLogicViewConfig;
  "magic-summary": boolean;
  "manual-work-os-syncs": boolean;
  "mark-orgs-from-wiz-issues": boolean;
  "max-dynamic-list-size": number;
  "max-kernels-per-org": number;
  "max-kernels-per-user": number;
  "max-signup-limit": number;
  "max-untrusted-kernels": number;
  "maybe-embed-hex-delay-ms": number;
  "mcp-get-thread-tool-config": McpGetThreadToolConfig;
  "mcp-org-tool-policy": boolean;
  "meticulous-rc-orgs": string[];
  "minerva-circuit-breaker-enabled": boolean;
  "minerva-epoch": number;
  /**
   * A dictionary of model feature keys to their configuration.
   * It should be type guarded against ModelFeatureConfig before being used.
   */
  "model-feature-config": unknown;
  /**
   * Maps a model to an ordered list of providers to try, scoped per AI client.
   * It should be validated against zModelProviderConfig before being used.
   */
  "model-provider-config": unknown;
  "new-csv-download": NewCsvDownloadConfig;
  "new-explorer-role": boolean;
  "no-max-signups": boolean;
  "no-op-garbage-collector-queues": boolean;
  "notification-chart-image-attachments": boolean;
  "o-auth-view-outputs-setting": boolean;
  "oauth-schemas-cache-enabled": boolean;
  "oauth-sensitive-slack-replies": boolean;
  "oauth-sensitivity-setting": boolean;
  "oauth-slack-private-replies": boolean;
  "onboarding-choose-your-own-adventure": boolean;
  /**
   * Array of ThreadExtractionEvalType values to run as online evals.
   * Default: ["TASK_COMPLETION", "CONTEXT_BASED_RESPONSE"]
   */
  "online-eval-rubrics-to-run": string[];
  "online-eval-sampling-percentage": number;
  "online-thread-eval-ui": boolean;
  "only-add-code-cells-on-first-run": boolean;
  "open-ai-rate-limit-config": OpenAiRateLimitConfig;
  "open-ai-safe-logging-errors": boolean;
  "optimize-explore-hql-date-filters": boolean;
  "org-creation-domain-rate-limit-config": unknown; // expected to be OrgCreationDomainRateLimitConfig
  "org-guides-indexing-enabled": boolean;
  "org-guides-replace-workspace-rules": boolean;
  "org-guides-search-tool-enabled": boolean;
  "orphaned-file-deletion-job-settings": unknown;
  "output-content-read-dynamo": boolean;
  "output-content-write-dynamo": boolean;
  "output-content-write-postgres": boolean;
  "password-users": boolean;
  /** ModelName values whose org-default use should warn admins of upcoming deprecation. */
  "pending-deprecation-model-names": string[];
  "pip-warn-use-uv-in-editor": boolean;
  "prewarm-kernels-ux-enablement": boolean;
  "product-launch-demo-hide-beta-flags": boolean;
  "project-source-prototype": boolean;
  "prompt-injection-window-size": number;
  "prompt-ordering-improvements": boolean;
  "public-api-ban-config": PublicApiBanConfig;
  "published-app-tv-mode-allowed": boolean;
  "pylon-chat": boolean;
  "pylon-chat-from-stripe-enabled": boolean;
  "quick-search-query-config": QueryConfig;
  "redis-reuse-connections": boolean;
  "reindex-es-batch-size": number;
  "reindex-schema-rate-limit-duration": number;
  "reindex-schema-rate-limit-points": number;
  "reserve-kernels-for-interactive": ReserveKernelsForInteractiveConfig;
  "resource-sync-to-compute-heartbeat-enabled": boolean;
  "retrieval-failure-detection-enabled": boolean;
  "retrieval-online-evals-included-in-ui": boolean;
  "review-agent-projects-as-context": boolean;
  "rules-migration-backfill-job": boolean;
  "rules-migration-update-shadowing": boolean;
  "run-project-api-rate-limit-config": ApiRateLimitConfig;
  "run-stats": boolean;
  "run-stats-show-errors": boolean;
  "scheduled-run-ai-summaries": boolean;
  "scheduled-run-concurrency": number;
  "scheduled-run-delay": number;
  "schema-refresh-bigquery-region-scope": boolean;
  "schema-refresh-data-service-batch-size": number;
  "schema-refresh-delete-batch-size": number;
  "schema-refresh-delta-mode": SchemaRefreshDeltaMode;
  // How many fully-streamed tables between mid-schema resync checkpoints. The
  // pending record buffers are flushed to the worklog before each checkpoint so
  // a checkpoint never marks a table processed before its diffs are durable.
  "schema-refresh-dfs-checkpoint-table-interval": number;
  // Max records buffered before the DFS traversal flushes a chunk to the worklog.
  "schema-refresh-dfs-traversal-chunk-size": number;
  "schema-refresh-disable-gc": boolean;
  "schema-refresh-download-grants-rate-limit-config": SchemaRefreshInMemoryRateLimitConfig;
  "schema-refresh-emit-dfs-partial-failures": boolean;
  "schema-refresh-gc-batch-size": number;
  "schema-refresh-gc-delay-ms": number;
  "schema-refresh-get-orphaned-entities-batch-size": number;
  "schema-refresh-get-schema-batch-size": number;
  "schema-refresh-interrupt": SchemaRefreshInterruptType;
  "schema-refresh-keep-failure-diff-rows-days": number;
  "schema-refresh-max-column-count": number;
  "schema-refresh-max-column-name-length": number;
  "schema-refresh-oauth-token-refresh-count": number;
  "schema-refresh-orphan-identification-threshold": SchemaRefreshOrphanIdentificationThreshold;
  "schema-refresh-rate-limit-duration": number;
  "schema-refresh-rate-limit-points": number;
  "schema-refresh-rate-limit-points-dfs": number;
  "schema-refresh-read-replica": boolean;
  "schema-refresh-skip-preloading": boolean;
  "schema-refresh-table-merkle-hash-max-buffered-columns": number;
  "schema-refresh-table-merkle-hash-persist": boolean;
  "schema-refresh-table-merkle-hash-read": boolean;
  "schema-refresh-update-db-batch-size": number;
  "schema-refresh-update-es-rate-limiter": SchemaRefreshInMemoryRateLimitConfig;
  "schema-refresh-update-es-table-column-batch-size": number;
  "schema-refresh-update-lance-batch-size": number;
  "schema-refresh-v3-rate-limiting": SchemaRefreshRateLimitConfig;
  "screenshot-connection-max-retries": number;
  "screenshot-extra-timeout": number;
  "screenshot-max-height": number;
  "screenshot-max-retries": number;
  "semantic-ingest-es-rate-limit-duration": SemanticIngestEsRateLimitConfig;
  "semantic-project-create-with-cli": boolean;
  "semantic-sql-kernel-expansion-enabled": boolean;
  "sidecar-batch-debounce-ms": number;
  "sidecar-health-monitoring-interval": number;
  "sidecar-mp-concurrency-limit": number;
  "sidecar-scheduling-executor": boolean;
  "sidecar-scheduling-executor-running-limit": number;
  "signed-embedding-api-rate-limit": number;
  "skip-non-dynamic-text-cells": boolean;
  "skip-thread-explore-transfer-on-user-deactivation": boolean;
  "slack-admin-discovery-popover": boolean;
  "slack-channel-query-limit": number;
  "slack-discovery-popover": boolean;
  "slack-files-read": boolean;
  /**
   * Adds the conversation-history OAuth scopes (channels/groups/mpim) to new
   * Slack installs. Must stay off until the Slack Marketplace listing is
   * approved for these scopes — requesting unapproved scopes can break the
   * install flow.
   */
  "slack-history-scopes": boolean;
  "slack-integration-v2": boolean;
  "slack-interactivity": boolean;
  "slack-message-admin-data-connection": boolean;
  "slack-proxy-service": boolean;
  "smoke-test-cleanup-cron": string;
  "smoke-test-cleanup-limit": number;
  "snowflake-semantic-sql": boolean;
  "snowflake-semantic-view-export": boolean;
  "sql-cell-preview-toggle": boolean;
  "st-prewarmed-defaults": PrewarmedDefaultInfo;
  "store-embedded-api-calls": boolean;
  "strip-output-contents-redux": unknown;
  "suggestion-dev-buttons": boolean;
  "suggestion-semantic-models": boolean;
  "suggestion-source-grouping-config": SuggestionSourceGroupingConfig;
  "support-user-sharing-cleanup-settings": unknown; // expected Partial<SupportUserSharingCleanupSettings>
  "table-deletion-queue-batch-size": number; // setting this to 0 will disable the queue
  "table-deletion-queue-cron": string;
  "temporal-background-review-worker-concurrency-config": TemporalBackgroundReviewWorkerConcurrencyConfig;
  "temporal-eval-worker-concurrency-config": TemporalEvalWorkerConcurrencyConfig;
  "temporal-hero-worker-concurrency-config": TemporalHeroWorkerConcurrencyConfig;
  "temporal-worker-concurrency-config": TemporalWorkerConcurrencyConfig;
  "thread-inspector-resources": boolean;
  "threads-copy-response": boolean;
  "threads-default-to-endorsed-mode-setting": boolean;
  "threads-flag-tool": boolean;
  "topic-workflow-config": TopicWorkflowConfig;
  "trial-limit-10-credits": boolean;
  "trino-session-user-passthrough": boolean;
  "typeahead-v-2": TypeaheadV2Config;
  "unified-context-preview": boolean;
  "unified-project-thread-ui": boolean;
  "use-click-house-jdbc-v1": boolean;
  "use-create-database-v2": boolean;
  "use-embedded-oauth-at-runtime": boolean;
  "use-in-house-ck": boolean;
  "use-only-register-ready-pods": boolean;
  "use-replica-db-if-available": boolean;
  "user-context-exhaust-classifier": boolean;
  "user-memory-extraction": boolean;
  "user-memory-injection": boolean;
  "usercheck-domain-denial-config": UsercheckDomainDenialConfig;
  "websocket-auth-caching": WebsocketAuthCachingConfig;
  "websocket-rate-limits": WebsocketRateLimitConfig;
  "workspace-default-projects-threads-sharing": boolean;
  "workspace-invite-links": boolean;
}

export type FeatureFlag = keyof FeatureFlagSet;

export const DEFAULT_FLAGS: FeatureFlagSet = {
  "additional-mariadb-params": false,
  "admin-specify-default-model": false,
  "agent-auto-data-connection": false,
  "agent-chat-garbage-collector-settings":
    defaultAgentChatGarbageCollectorSettings,
  "agent-data-app-console-log-strategy": DEFAULT_DATA_APP_CONSOLE_LOG_STRATEGY,
  "agent-data-app-screenshot-strategy": DEFAULT_DATA_APP_SCREENSHOT_STRATEGY,
  "agent-desk": false,
  "agent-desk-gen-app-writes": false,
  "agent-desktop-notifications": false,
  "agent-eager-input-streaming": true,
  "agent-knowledge-search-tool": false,
  "agent-fast-mode": false,
  "agent-file-download-links": false,
  "agent-llm-retry-config": DEFAULT_AGENT_LLM_RETRY_CONFIG,
  "agent-model-effort-picker": false,
  "agent-prompt-cache-routing": DEFAULT_AGENT_PROMPT_CACHE_ROUTING_CONFIG,
  "agent-plain-code-project-capture-strategy":
    DEFAULT_PLAIN_CODE_PROJECT_CAPTURE_STRATEGY,
  "agent-run-scopes": false,
  "agent-run-sql-default-timeout-seconds": DEFAULT_RUN_SQL_TIMEOUT_SECONDS,
  "agent-slack-thread-context-channel-access-check": false,
  "agent-slack-thread-context-tool": false,
  "agent-slackbot-public-channel-share-check": false,
  "agent-stale-thread-warning-config":
    DEFAULT_AGENT_STALE_THREAD_WARNING_CONFIG,
  "agent-main-chart-tool": false,
  "agent-stop-cell-tool": false,
  "agent-tasks": false,
  "agent-tasks-create-tool": false,
  "agent-tasks-cleanup-job-settings": defaultTaskCleanupSettings,
  "agent-tasks-expiration-job-settings": defaultTaskExpirationSettings,
  "agent-thread-artifacts": false,
  "agent-thread-artifacts-frontend": false,
  "agent-thread-model-stamping": false,
  "agent-thread-viewport-fill-pagination": false,
  "agent-view-tables-column-regex": false,
  "agent-wait-for-cell-config": DEFAULT_AGENT_WAIT_FOR_CELL_CONFIG,
  "ai-billing-cost-alert-thresholds": defaultAiBillingCostAlertThresholds,
  "ai-billing-enforcement-activation-orgs": {
    maxOrgsPerRun: 20,
    mode: "ORG_IDS",
    orgIds: [],
  },
  "ai-billing-feature-exemption": {
    [FeatureExemptBillingReason.DATA_APP_SUBAGENT]: true,
    [FeatureExemptBillingReason.REVIEW_AGENT]: true,
    [FeatureExemptBillingReason.ARTIFACT_EXTRACTION_AGENT]: true,
    [FeatureExemptBillingReason.HERO_ANALYZER_AGENT]: true,
    [FeatureExemptBillingReason.HERO_AGGREGATOR_AGENT]: true,
    [FeatureExemptBillingReason.KNOWLEDGE_CURATION_AGENT]: true,
    [FeatureExemptBillingReason.VISUALIZATION_SUBAGENT]: true,
  },
  "ai-billing-internal-orgs": false,
  "ai-credit-enforcement": true,
  "ai-credit-topup-consolidate-invoice-items": false,
  "ai-credit-topup-immediate-charge": false,
  "allow-data-retention-models": false,
  "allow-duplication-for-project": false,
  "allow-embedded-user": false,
  "allow-fable-model": false,
  "allow-public-domain-signups": false,
  "allow-r-project-creation": false,
  "allow-semantic-endorsed-filter": false,
  "ao-rate-limit": DEFAULT_AO_RATE_LIMIT_CONFIG,
  "app-builder-last-element-resize": false,
  "app-loading-bar-delay-seconds": 4,
  "app-loading-bar-initial-percent": 10,
  "app-run-button-memory": false,
  "app-run-button-progress": false,
  "app-session-a-a": false,
  "app-session-event-batching-config": DEFAULT_APP_SESSION_BATCHING_CONFIG,
  "app-session-events-config": DEFAULT_APP_SESSION_EVENTS_CONFIG,
  "app-session-garbage-collector-settings":
    defaultAppSessionGarbageCollectorSettings,
  "app-session-mp-client-logging": false,
  "artifact-extraction-agent-intent-summary-tools": false,
  "ask-agent-slack-bot": false,
  "audit-log-llm-requests-enabled": false,
  "audit-log-workos-backfill": { enabled: false },
  "audit-log-workos-backfill-interrupt": false,
  "auth-fanout-diagnostic-threshold": 5000,
  "auto-rebuild-custom-images-delay": 250,
  "auto-rebuild-docker-images": false,
  "auto-refresh-client-config": { enabled: false },
  "backfill-embedded-oauth-credentials": false,
  "backfill-indexing-page-size": 500,
  "backfill-indexing-plans": [],
  "base-kernel-image-gate": {
    defaultPythonKernelImage: "python39",
    enabled: ["python311", "python310", "python39"],
    python39NullCutoffDate: "2025-03-30T00:00:00.000Z",
  },
  "baseten-embedding-model-key": "openai_fallback",
  "bedrock-custom-model-mapping": false,
  "bulk-deactivate-users-by-domain": false,
  "bull-queue-job-timeout-override": {},
  "bull-queue-repair-maxed-groups": false,
  "bull-queue-slow-job-threshold-override": {},
  "byok-bedrock": false,
  "byok-enabled-override": false,
  "byok-vertex": false,
  "cell-compilation-concurrency": 64,
  "cell-compilation-merge-stages": false,
  "cell-compilation-timeout-ms": 60000,
  "cell-debugger": false,
  "cell-references-fix-migration": false,
  "chart-cheat-sheet": false,
  "chat-with-generative-app": false,
  "check-kernel-limits": true,
  "clickhouse-client-buffer-size": 1_048_576 * 5,
  "clickhouse-json-column": false,
  "clickhouse-query-timeout-seconds": 350,
  "client-devtools": DEFAULT_CLIENT_DEVTOOLS_FLAG_CONFIG,
  "collaborative-project-threads": false,
  "compute-action-garbage-collector-settings":
    defaultComputeActionGarbageCollectorSettings,
  "compute-heartbeat-send-to-segment-enabled": false,
  "compute-heartbeat-sync-to-stripe-cron-schedule": "*/5 * * * *", // default to every 5 minutes
  "compute-heartbeat-sync-to-stripe-mode": "test",
  "compute-heartbeat-sync-to-stripe-status": "JOB_DISABLED",
  "connections-per-trace-debounce-ms": 10_000,
  "connections-per-trace-warning-threshold": 10,
  "context-studio-saved-views": false,
  "cp-diff-tracking-cron-schedule": "*/1 * * * *",
  "cp-diff-tracking-delay-threshold-ms": 100,
  "cp-diff-tracking-enabled": false,
  "cp-kernel-management": DefaultKernelManagementCPMigrationState,
  "cryptominer-regex": "",
  "csv-download-standard-data-service-auth": false,
  "custom-kernels-new-auto-rebuild-logic": false,
  "custom-kernels-ui": false,
  "custom-kernels-ui-v2": false,
  "data-app-chart-csv-download": false,
  "data-app-file-ao-editing": false,
  "data-conn-diff-schema-update": false,
  "data-conn-diff-schema-update-threshold": 0.1,
  "data-conn-sleep-times": { sleepEvery: 10000, sleepForMs: 10 },
  "data-connection-athena-sql-schema-discovery": false,
  "data-connection-clickhouse-chdb-allowed": false,
  "data-connection-databricks-proxy": false,
  "data-connection-deletion-job-limit": 10_000,
  "data-connection-motherduck-enable-read-scaling": true,
  "data-connection-snowflake-async-queries": false,
  "data-connection-types-to-limit": {},
  "data-connections-header-cleanup": false,
  "data-recommended-tab": false,
  "data-retention-available": false,
  "data-service-dfs-batch-size": 7500,
  "data-service-dfs-max-bytes-per-batch": 1024 * 1024 * 10, // 10MB
  "data-service-skip-hex-row-index": false,
  "data-service-table-load-batch-size": 100,
  "data-source-shard-deletion-queue-config": {
    batchSizes: {
      categoryDeleteBatchSize: 0,
      dataConnectionDeleteBatchSize: 0,
      statusUpdateBatchSize: 0,
    },
    cron: "*/10 * * * *",
    peekBatchSize: 0,
  },
  "database-backfill-cron": "",
  "database-backfill-queries": DEFAULT_BACKFILL_CONFIG,
  "dbt-data-conn-sleep-times": { sleepEvery: 10000, sleepForMs: 10 },
  "dbt-disallow-proxy-server": false,
  "dbt-fetch-batch-size": 500,
  "dbt-timeout-metadata": 5000,
  "dd-retain-execution-trace": false,
  "deadlock-detector-timeout-s": 60,
  "debug-gh-sync": false,
  "default-api-rate-limit-config": DEFAULT_BASE_API_RATE_LIMIT_CONFIG,
  "default-display-table": false,
  "delete-app-session-api-client-on-kill": false,
  "disable-allow-duplicate-columns": false,
  "disable-ao-garbage-collector": false,
  "disable-bull-queue-job-timeout": false,
  "disable-chat-support-for-company-closure": {
    available: true,
    cta: "",
    message: "",
  },
  "disable-context-changeset-garbage-collector": false,
  "disable-count-query-for-deletion-utils": false,
  "disable-dereference-agent-client-to-user": false,
  "disable-embedded-user-api-client-garbage-collector": false,
  "disable-execution-event-writes": false,
  "disable-hex-garbage-collector": false,
  "disable-hex-version-default-app-session-garbage-collector": false,
  "disable-legacy-execution-events-storage": false,
  "disable-presigned-embed-url-garbage-collector": false,
  "disable-safe-logging-for-agent-provider-errors": false,
  "disable-table-preview-api-client-garbage-collector": false,
  "disable-workspace-sharing": false,
  // This is not the default on LD, but if we fail to pull this flag
  // we want to err on the side of _not_ accidentally charging customers.
  "do-not-charge-for-kernels-created-by-hex": true,
  "dwh-table-search-config": {
    fts: {
      keywordBoost: 1.3,
    },
    rerank: {
      enableEndorsedSemanticGroup: false,
      rrf: {
        c: 60,
      },
      weights: {
        endorsedBoost: 2,
        endorsedSemantic: 1.0,
        fts: 0.4,
        semantic: 0.6,
      },
    },
  },
  "editable-data-app-charts": false,
  "embedded-user-notification-can-use-custom-cron": false,
  "embedded-user-notification-limit": 3,
  "embeddings-baseten-model-keys": {
    "snowflake-arctic-embed-l-v2.0": "yqvypjgq", // dev
  },
  "embeddings-garbage-collection": false,
  "embeddings-garbage-collection-deletion-batch-size": 10,
  "embeddings-garbage-collection-search-batch-size": 100,
  "embeddings-mpk-config": {
    [LanceEmbeddingProvider.OPENAI]: {
      [OpenAIEmbeddingModel["text-embedding-ada-002"]]: {
        batchSize: 500,
        concurrency: 5,
      },
    },
  },
  "embeddings-orphan-deletion": false,
  "embeddings-orphan-deletion-batch-size": 10,
  "embeddings-orphan-search-minimum-batch-size": 100,
  "embeddings-project-retrieval-mpk": {
    ...DEFAULT_MPK,
    schemaVersion: LanceTableOfHexesSchemaVersionEnum["1"],
  },
  "embeddings-table-retrieval-mpk": DEFAULT_MPK,
  "emit-dbt-data-size-metric": false,
  "enable-agent-threads-indexing": false,
  "enable-auth-fanout-diagnostic": false,
  "enable-committed-credit-grant-sync": false,
  "enable-cross-connection-table-search": false,
  "enable-cross-request-semantic-cap-cache": false,
  "enable-cryptominer-regex": false,
  "enable-data-service-csv-sanitization": false,
  "enable-delete-pods-from-wiz-issues": false,
  "enable-embedding-app-freshness-options": false,
  "enable-exec-events-sideeffects": false,
  "enable-execution-events-s3-archive": false,
  "enable-external-file-integration-kernel-api": false,
  "enable-hex-cli-endpoints": false,
  "enable-iam-role-based-ecr-repo-authentication": false,
  "enable-iam-role-based-s3-integration-authentication": false,
  "enable-kernel-write-parquet": false,
  "enable-monitor-replication-slots": false,
  "enable-oauth-schema-auth": false,
  "enable-open-in-agent-dev-tools": false,
  "enable-read-execution-events-from-s3": false,
  "enable-reindex-schema": false,
  "enable-rerank-search-users": false,
  "enable-run-project-with-trace": false,
  "enable-scheduled-run-retry": false,
  "enable-semantic-cap-entity-memo": false,
  "enable-snowflake-oauth-sso": false,
  "enable-sql-completion-logs": false,
  "enable-sync-field-auth-fast-path": false,
  "enable-workspace-thread-search": false,
  "enforce-compute-spend-limits": false,
  "es-bulk-delete-chunk-size": 300,
  "es-bulk-timeout-schema-refresh": "1m",
  "es-delete-all-datasources-delay-ms": 10000,
  "es-delete-by-query-timeout": "1m",
  "es-index-config": {
    datasourceSchemas: {
      backfillIndexes: ["datasource_schemas-v0"],
      readerIndex: "datasource_schemas-v0",
      writerIndexes: ["datasource_schemas-v0"],
    },
    projects: {
      backfillIndexes: ["projects"],
      readerIndex: "projects",
      writerIndexes: ["projects"],
    },
  },
  "es-index-reader-config": {
    datasourceSchemas: "datasource_schemas-v0",
    projects: "projects",
  },
  "es-reindex-config": {
    initialIdOffset: {},
  },
  "es-sync-batch-size": 250,
  "es-sync-force-to-head-when-stuck": true,
  "es-sync-hex-chunk-size": 10,
  "es-sync-hex-job-limit": 100,
  "es-sync-max-batch-size": 20000,
  "experimentation-test": false,
  "explore-sankey": false,
  // Deprecated: Use Org.explorerRoleEnabled instead.
  "explorer-role": false,
  "fast-forward-implementation": DEFAULT_FAST_FORWARD_IMPLEMENTATION,
  "flag-data-team-button": false,
  "flat-subagent-workflow": false,
  "force-live-updates-for-flags": [],
  "force-output-iframe-blob": false,
  "force-redefinition-edges": false,
  "garbage-collection-rate-limit-config":
    DEFAULT_GARBAGE_COLLECTION_RATE_LIMIT_CONFIG,
  "gen-apps-projects-as-context": false,
  "general-api-rate-limit-config": DEFAULT_GENERAL_API_RATE_LIMIT_CONFIG,
  "grant-access-for-project-from-slack": false,
  "headless-notebook-agent-threads": false,
  "hero-pipeline-config": defaultHeroPipelineConfig,
  "hex-garbage-collection-app-session-cell-limit": 1000,
  "hex-garbage-collection-hex-version-limit": 100,
  "hex-to-es-sync-mode": HexToESSyncMode.DISABLED,
  "hex-version-autosave-gc-settings":
    defaultHexVersionAutosaveGarbageCollectorSettings,
  "hide-unsubscribe-link-for-sedgwick-in-embeds": false,
  "hisc-async-config": { enableBetweenCells: false, enableWithinCell: false },
  "hero-frontend": false,
  "home-page-suggestions": false,
  "hourly-schedule-limit": 12,
  "hybrid-index-config": defaultHybridIndexConfig,
  "idle-kernel-expiry-settings": defaultIdleKernelExpirySettings,
  "in-app-announcements": [],
  "in-app-releases": { on: false },
  "in-app-whats-new": { on: false },
  "internal-test-collection-job-delay-ms": 5000,
  "internal-test-collection-queue": DEFAULT_INTERNAL_TEST_COLLECTION_QUEUE,
  "kernel-init-timeout-ms": 5_000,
  "kernel-lifetime-stats": false,
  "kernel-metric-logger": true,
  "kernel-metric-logger-code": true,
  "knowledge-dev-tools": false,
  "lance_db-sync-limiter-settings": defaultLanceSyncQueueLimiterSettings,
  "lance-db-data-conn-backfill-data-conn-to-skip": [],
  "lance-db-data-conn-backfill-frequency-ms": 5 * 60 * 1000, // 5 minutes
  "lance-db-data-conn-backfill-limit": 0,
  "lance-db-data-conn-backfill-retry-limit": 1,
  "lance-db-data-conn-errored-actions-limit": 0,
  "lance-db-data-conn-stuck-in-backfilling-limit": 10,
  "lance-db-entity-promotion-limit": 0,
  "lance-db-hex-backfill": false,
  "lance-db-hex-backfill-excluded-orgs": [],
  "lance-db-hex-backfill-frequency-ms": 5 * 60 * 1000, // 5 minutes
  "lance-db-hex-backfill-limit": 0,
  "lance-db-hex-errored-actions-limit": 0,
  "lance-db-hex-max-sync-attempts": 5,
  "lance-db-hex-stuck-in-backfilling-limit": 0,
  "lance-db-hex-stuck-in-processing-limit": 0,
  "lance-db-long-running-action-threshold-hours": 12,
  "lance-db-sync-batch-lance-actions-queue-interval-ms": 5 * 60 * 1000, // 5 minutes
  "lance-db-sync-batch-size": 50000,
  "lance-db-table-retrieval-limit": 30,
  "lance-db-table-retrieval-timeout-ms": 5000,
  "lance-db-un-indexed-rows-threshold": 10_000,
  "lance-db-where-batch-size": 10000,
  "legacy-git-sync": false,
  "log-dbt-metadata-counts": false,
  "logic-session-timeout-extension": false,
  "magic-esignature": false,
  "magic-lance-project-retrieval-limit": 30,
  "magic-lance-project-retrieval-vector-distance-threshold": 0,
  "magic-max-other-dfs": 25,
  "magic-notebook-logic-view-config": defaultMagicNotebookLogicViewConfig,
  "magic-summary": false,
  "manual-work-os-syncs": true,
  "mark-orgs-from-wiz-issues": false,
  "max-dynamic-list-size": 10_000,
  "max-kernels-per-org": 500,
  "max-kernels-per-user": 25,
  "max-signup-limit": 10,
  "max-untrusted-kernels": -1, // unlimited
  "maybe-embed-hex-delay-ms": 600000, // 10 minutes
  "mcp-get-thread-tool-config": {
    maxThreadStatusChecks: 10,
    threadStatusCheckIntervalMs: 2000,
  },
  "mcp-org-tool-policy": false,
  "meticulous-rc-orgs": [],
  "minerva-circuit-breaker-enabled": false,
  "minerva-epoch": 0,
  "model-feature-config": DEFAULT_MODEL_FEATURE_CONFIG,
  "model-provider-config": DEFAULT_MODEL_PROVIDER_CONFIG,
  "new-csv-download": {
    disableFallback: false,
    enableNewPath: true,
    newPathCells: [
      CellType.CHART,
      CellType.EXPLORE,
      CellType.PIVOT,
      CellType.SQL,
      CellType.DISPLAY_TABLE,
    ],
  },
  "new-explorer-role": false,
  "no-max-signups": false,
  "no-op-garbage-collector-queues": false,
  "notification-chart-image-attachments": false,
  "o-auth-view-outputs-setting": false,
  "oauth-schemas-cache-enabled": true,
  "oauth-sensitivity-setting": false,
  "oauth-sensitive-slack-replies": false,
  "oauth-slack-private-replies": false,
  "onboarding-choose-your-own-adventure": false,
  "online-eval-rubrics-to-run": ["TASK_COMPLETION", "CONTEXT_BASED_RESPONSE"],
  "online-eval-sampling-percentage": 10,
  "online-thread-eval-ui": false,
  "only-add-code-cells-on-first-run": false,
  "open-ai-rate-limit-config": DEFAULT_OPEN_AI_RATE_LIMIT_CONFIG,
  "open-ai-safe-logging-errors": false,
  "optimize-explore-hql-date-filters": false,
  "org-creation-domain-rate-limit-config":
    defaultOrgCreationDomainRateLimitConfig,
  "org-guides-indexing-enabled": false,
  "org-guides-replace-workspace-rules": false,
  "org-guides-search-tool-enabled": false,
  "orphaned-file-deletion-job-settings": {},
  "output-content-read-dynamo": false,
  "output-content-write-dynamo": false,
  "output-content-write-postgres": true,
  "password-users": false,
  "pending-deprecation-model-names": [],
  "pip-warn-use-uv-in-editor": false,
  "agent-pre-workflow-setup": false,
  "prewarm-kernels-ux-enablement": false,
  "product-launch-demo-hide-beta-flags": false,
  "project-source-prototype": false,
  "prompt-injection-window-size": 10,
  "prompt-ordering-improvements": false,
  "public-api-ban-config": { isBanned: false },
  "published-app-tv-mode-allowed": false,
  "pylon-chat": false,
  "pylon-chat-from-stripe-enabled": false,
  "quick-search-query-config": DEFAULT_QUERY_CONFIG,
  "redis-reuse-connections": false,
  "reindex-es-batch-size": 500,
  "reindex-schema-rate-limit-duration": 1,
  "reindex-schema-rate-limit-points": 1,
  "reserve-kernels-for-interactive":
    DEFAULT_RESERVE_KERNELS_FOR_INTERACTIVE_CONFIG,
  "resource-sync-to-compute-heartbeat-enabled": false,
  "retrieval-failure-detection-enabled": false,
  "retrieval-online-evals-included-in-ui": false,
  "review-agent-projects-as-context": false,
  "rules-migration-backfill-job": false,
  "rules-migration-update-shadowing": false,
  "run-project-api-rate-limit-config":
    DEFAULT_RUN_PROJECT_API_RATE_LIMIT_CONFIG,
  "run-stats": false,
  "run-stats-show-errors": false,
  "scheduled-run-ai-summaries": false,
  "scheduled-run-concurrency": 1,
  "scheduled-run-delay": 60000,
  "schema-refresh-bigquery-region-scope": false,
  "schema-refresh-data-service-batch-size": 10_000,
  "schema-refresh-delete-batch-size": 1000,
  "schema-refresh-delta-mode": "off",
  "schema-refresh-dfs-checkpoint-table-interval": 50,
  "schema-refresh-dfs-traversal-chunk-size": 20_000,
  "schema-refresh-disable-gc": false,
  "schema-refresh-download-grants-rate-limit-config": {
    batchSize: 2_000,
    duration: 1,
    points: 1,
  },
  "schema-refresh-emit-dfs-partial-failures": false,
  "schema-refresh-gc-batch-size": 100,
  "schema-refresh-gc-delay-ms": 1000,
  "schema-refresh-get-orphaned-entities-batch-size": 1000,
  "schema-refresh-get-schema-batch-size": 1000,
  "schema-refresh-interrupt": "NO_INTERRUPT",
  "schema-refresh-keep-failure-diff-rows-days": 0,
  "schema-refresh-max-column-count": 2_000_000,
  "schema-refresh-max-column-name-length": 256,
  "schema-refresh-oauth-token-refresh-count": 5,
  "schema-refresh-orphan-identification-threshold": {
    entities: 1_000,
    parents: 100,
  },
  "schema-refresh-rate-limit-duration": 1,
  "schema-refresh-rate-limit-points": 1,
  "schema-refresh-rate-limit-points-dfs": 1000,
  "schema-refresh-read-replica": false,
  "schema-refresh-table-merkle-hash-max-buffered-columns": 50_000,
  "schema-refresh-table-merkle-hash-persist": false,
  "schema-refresh-table-merkle-hash-read": false,
  "schema-refresh-skip-preloading": false,
  "schema-refresh-update-db-batch-size": 5000,
  "schema-refresh-update-es-rate-limiter": {
    batchSize: 2_000,
    duration: 1,
    points: 1,
  },
  "schema-refresh-update-es-table-column-batch-size": 10,
  "schema-refresh-update-lance-batch-size": 5000,
  "schema-refresh-v3-rate-limiting": DEFAULT_SCHEMA_REFRESH_RATE_LIMIT_CONFIG,
  "screenshot-connection-max-retries": 2,
  "screenshot-extra-timeout": 5000,
  "screenshot-max-height": 15000,
  "screenshot-max-retries": 1,
  "semantic-ingest-es-rate-limit-duration": {
    duration: 1,
    points: 5,
  },
  "semantic-project-create-with-cli": false,
  "semantic-sql-kernel-expansion-enabled": false,
  "sidecar-batch-debounce-ms": 500,
  "sidecar-health-monitoring-interval": 30_000,
  "sidecar-mp-concurrency-limit": 25,
  "sidecar-scheduling-executor": false,
  "sidecar-scheduling-executor-running-limit": 8,
  "signed-embedding-api-rate-limit": 20,
  "skip-non-dynamic-text-cells": false,
  "skip-thread-explore-transfer-on-user-deactivation": false,
  "slack-admin-discovery-popover": false,
  "slack-channel-query-limit": 30,
  "slack-discovery-popover": false,
  "slack-files-read": false,
  "slack-history-scopes": false,
  "slack-integration-v2": false,
  "slack-interactivity": false,
  "slack-message-admin-data-connection": false,
  "slack-proxy-service": false,
  "smoke-test-cleanup-cron": "30 * * * *", // default to every 30 min past the hour
  "smoke-test-cleanup-limit": 50,
  "snowflake-semantic-sql": false,
  "snowflake-semantic-view-export": false,
  "sql-cell-preview-toggle": false,
  "st-prewarmed-defaults": NoPrewarmedImages,
  "store-embedded-api-calls": false,
  "strip-output-contents-redux": DEFAULT_STRIP_OUTPUT_CONTENTS_CONFIG,
  "suggestion-dev-buttons": false,
  "suggestion-semantic-models": false,
  "suggestion-source-grouping-config": defaultSuggestionSourceGroupingConfig,
  "support-user-sharing-cleanup-settings":
    defaultSupportUserSharingCleanupSettings,
  "table-deletion-queue-batch-size": 1000,
  "table-deletion-queue-cron": "*/10 * * * *", // default to every 10 minutes
  "temporal-background-review-worker-concurrency-config":
    defaultTemporalBackgroundReviewWorkerConcurrencyConfig,
  "temporal-eval-worker-concurrency-config":
    defaultTemporalEvalWorkerConcurrencyConfig,
  "temporal-hero-worker-concurrency-config":
    defaultTemporalHeroWorkerConcurrencyConfig,
  "temporal-worker-concurrency-config": defaultTemporalWorkerConcurrencyConfig,
  "thread-inspector-resources": false,
  "threads-copy-response": false,
  "threads-default-to-endorsed-mode-setting": false,
  "threads-flag-tool": false,
  "topic-workflow-config": defaultTopicWorkflowConfig,
  "trial-limit-10-credits": false,
  "trino-session-user-passthrough": false,
  "typeahead-v-2": {
    enabledPython: false,
    enabledSql: false,
    modelKeys: {
      dev: "",
      eu: "",
      us: "",
    },
  },
  "unified-context-preview": false,
  "unified-project-thread-ui": false,
  "use-click-house-jdbc-v1": false,
  "use-create-database-v2": false,
  "use-embedded-oauth-at-runtime": false,
  "use-in-house-ck": false,
  "use-only-register-ready-pods": true,
  "use-replica-db-if-available": false,
  "user-context-exhaust-classifier": false,
  "user-memory-extraction": false,
  "user-memory-injection": false,
  "usercheck-domain-denial-config": defaultUsercheckDomainDenialConfig,
  "websocket-auth-caching": { timeout: null },
  "websocket-rate-limits": {},
  "workspace-default-projects-threads-sharing": false,
  "workspace-invite-links": false,
  ...NewUserFeatureFlagDefaults,
};

export enum FlagConfigOverrideSet {
  DEV_EXAMPLE = "DEV_EXAMPLE",
  DEV_EXAMPLE_2 = "DEV_EXAMPLE_2",
  JUPYTER = "JUPYTER",
}

export const FlagConfigOverride: Record<
  FlagConfigOverrideSet,
  Partial<FeatureFlagSet>
> = {
  DEV_EXAMPLE: {
    "experimentation-test": true,
  },
  DEV_EXAMPLE_2: {
    "experimentation-test": false,
  },
  JUPYTER: {},
};

export type FeatureFlagOverrideFn<T> = ({
  email,
  env,
  orgId,
  stackId,
}: {
  email?: string;
  env?: ProductionEnvironment;
  orgId?: OrgId;
  stackId?: string;
}) => T | undefined;
export type FeatureFlagOverride<T> = T | FeatureFlagOverrideFn<T>;

export type FeatureFlagOverrides = {
  [K in keyof FeatureFlagSet]?: FeatureFlagOverride<FeatureFlagSet[K]>;
};

// When we explicitly disable launch darkly, we include a set of offline
// overrides to manage behavior. This is specifically relevant to running E2E
// tests, where we want to avoid inflating our MAUs in LD but still support
// having multiple conditions under test.
export const LaunchDarklyDisabledOverrides: FeatureFlagOverrides = {
  "additional-mariadb-params": true,
  "allow-duplication-for-project": true,
  "allow-embedded-user": true,
  "allow-public-domain-signups": false,
  // Keep R project creation enabled under the LD-disabled overrides so that
  // E2E tests (which run with LAUNCH_DARKLY_DISABLED=true) can still exercise
  // the "Create R project" flows in the UI.
  "allow-r-project-creation": true,
  "allow-semantic-endorsed-filter": false,
  "auto-rebuild-custom-images-delay": 250,
  "auto-rebuild-docker-images": false,
  // Hardcode the python version used---in CI, we only install the 3.11 environment
  "base-kernel-image-gate": {
    defaultPythonKernelImage: "python311",
    enabled: ["python311"],
    python39NullCutoffDate: "2025-03-30T00:00:00.000Z",
  },
  "bull-queue-slow-job-threshold-override": {},
  "collaborative-project-threads": false,
  "compute-action-garbage-collector-settings":
    defaultComputeActionGarbageCollectorSettings,
  "compute-heartbeat-send-to-segment-enabled": false,
  "compute-heartbeat-sync-to-stripe-cron-schedule": "*/5 * * * *", // default to every 5 minutes
  "compute-heartbeat-sync-to-stripe-mode": "test",
  "compute-heartbeat-sync-to-stripe-status": "JOB_DISABLED",
  "cp-diff-tracking-cron-schedule": "*/1 * * * *",
  "cp-diff-tracking-delay-threshold-ms": 100,
  "cp-diff-tracking-enabled": false,
  "cp-kernel-management": DefaultKernelManagementCPMigrationState,
  "custom-kernels-ui": false,
  "custom-kernels-ui-v2": false,
  "data-app-file-ao-editing": false,
  "data-conn-diff-schema-update": false,
  "data-conn-diff-schema-update-threshold": 0.1,
  "data-conn-sleep-times": { sleepEvery: 10000, sleepForMs: 10 },
  "dbt-data-conn-sleep-times": { sleepEvery: 10000, sleepForMs: 10 },
  "debug-gh-sync": true,
  "delete-app-session-api-client-on-kill": false,
  "disable-ao-garbage-collector": false,
  "disable-embedded-user-api-client-garbage-collector": false,
  "disable-hex-garbage-collector": false,
  "disable-hex-version-default-app-session-garbage-collector": false,
  "disable-presigned-embed-url-garbage-collector": false,
  "disable-table-preview-api-client-garbage-collector": false,
  "disable-workspace-sharing": false,
  "emit-dbt-data-size-metric": false,
  "enable-delete-pods-from-wiz-issues": false,
  "enable-hex-cli-endpoints": false,
  "enable-reindex-schema": false,
  "enable-rerank-search-users": false,
  "enable-run-project-with-trace": false,
  "enable-scheduled-run-retry": false,
  "enable-sql-completion-logs": false,
  "enforce-compute-spend-limits": false,
  "es-bulk-delete-chunk-size": 300,
  "es-bulk-timeout-schema-refresh": "1m",
  "es-delete-all-datasources-delay-ms": 10000,
  "es-delete-by-query-timeout": "1m",
  "es-index-config": {
    datasourceSchemas: {
      backfillIndexes: ["datasource_schemas-v0"],
      readerIndex: "datasource_schemas-v0",
      writerIndexes: ["datasource_schemas-v0"],
    },
    projects: {
      backfillIndexes: ["projects"],
      readerIndex: "projects",
      writerIndexes: ["projects"],
    },
  },
  "es-index-reader-config": {
    datasourceSchemas: "datasource_schemas-v0",
    projects: "projects",
  },
  "es-reindex-config": {
    initialIdOffset: {},
  },
  "es-sync-hex-chunk-size": 10,
  "es-sync-hex-job-limit": 100,
  "explore-sankey": false,
  "force-redefinition-edges": true,
  "headless-notebook-agent-threads": false,
  "hex-to-es-sync-mode": HexToESSyncMode.DISABLED,
  "internal-test-collection-job-delay-ms": 5000,
  "internal-test-collection-queue": DEFAULT_INTERNAL_TEST_COLLECTION_QUEUE,
  "kernel-lifetime-stats": false,
  "lance_db-sync-limiter-settings": defaultLanceSyncQueueLimiterSettings,
  "lance-db-data-conn-backfill-data-conn-to-skip": [],
  "lance-db-data-conn-backfill-frequency-ms": 5 * 60 * 1000, // 5 minutes
  "lance-db-data-conn-backfill-limit": 0,
  "lance-db-data-conn-backfill-retry-limit": 1,
  "lance-db-data-conn-errored-actions-limit": 0,
  "lance-db-data-conn-stuck-in-backfilling-limit": 10,
  "lance-db-hex-backfill": false,
  "lance-db-hex-backfill-excluded-orgs": [],
  "lance-db-hex-backfill-frequency-ms": 5 * 60 * 1000, // 5 minutes
  "lance-db-hex-backfill-limit": 0,
  "lance-db-hex-errored-actions-limit": 0,
  "lance-db-hex-max-sync-attempts": 5,
  "lance-db-hex-stuck-in-backfilling-limit": 0,
  "lance-db-hex-stuck-in-processing-limit": 0,
  "lance-db-long-running-action-threshold-hours": 12,
  "lance-db-sync-batch-lance-actions-queue-interval-ms": 5000,
  "lance-db-sync-batch-size": 50000,
  "lance-db-table-retrieval-limit": 30,
  "lance-db-table-retrieval-timeout-ms": 5000,
  "lance-db-un-indexed-rows-threshold": 10_000,
  "lance-db-where-batch-size": 10000,
  "legacy-git-sync": false,
  "magic-esignature": false,
  "magic-lance-project-retrieval-limit": 30,
  "magic-lance-project-retrieval-vector-distance-threshold": 0,
  "magic-max-other-dfs": 25,
  "magic-summary": false,
  "manual-work-os-syncs": true,
  "mark-orgs-from-wiz-issues": false,
  "max-signup-limit": 10,
  "maybe-embed-hex-delay-ms": 600000, // 10 minutes
  "meticulous-rc-orgs": [],
  "new-explorer-role": false,
  "no-max-signups": false,
  "no-op-garbage-collector-queues": false,
  "only-add-code-cells-on-first-run": true,
  "optimize-explore-hql-date-filters": false,
  "password-users": false,
  "prewarm-kernels-ux-enablement": false,
  "prompt-injection-window-size": 10,
  "quick-search-query-config": DEFAULT_QUERY_CONFIG,
  "redis-reuse-connections": false,
  "reindex-es-batch-size": 500,
  "reindex-schema-rate-limit-duration": 1,
  "reindex-schema-rate-limit-points": 1,
  "resource-sync-to-compute-heartbeat-enabled": false,
  "scheduled-run-ai-summaries": false,
  "schema-refresh-data-service-batch-size": 10_000,
  "schema-refresh-disable-gc": false,
  "schema-refresh-download-grants-rate-limit-config": {
    batchSize: 2_000,
    duration: 1,
    points: 1,
  },
  "schema-refresh-gc-batch-size": 100,
  "schema-refresh-gc-delay-ms": 1000,
  "schema-refresh-get-orphaned-entities-batch-size": 1000,
  "schema-refresh-get-schema-batch-size": 1000,
  "schema-refresh-interrupt": "NO_INTERRUPT",
  "schema-refresh-max-column-count": 2_000_000,
  "schema-refresh-max-column-name-length": 256,
  "schema-refresh-rate-limit-duration": 1,
  "schema-refresh-rate-limit-points": 1,
  "schema-refresh-read-replica": false,
  "schema-refresh-table-merkle-hash-max-buffered-columns": 50_000,
  "schema-refresh-table-merkle-hash-persist": false,
  "schema-refresh-table-merkle-hash-read": false,
  "schema-refresh-update-db-batch-size": 5000,
  "schema-refresh-update-es-rate-limiter": {
    batchSize: 2_000,
    duration: 1,
    points: 1,
  },
  "schema-refresh-update-es-table-column-batch-size": 10,
  "schema-refresh-update-lance-batch-size": 5000,
  "screenshot-connection-max-retries": 2,
  "screenshot-extra-timeout": 5000,
  "screenshot-max-height": 15000,
  "screenshot-max-retries": 1,
  "skip-non-dynamic-text-cells": false,
  "skip-thread-explore-transfer-on-user-deactivation": false,
  "slack-channel-query-limit": 30,
  "st-prewarmed-defaults": NoPrewarmedImages,
  "suggestion-source-grouping-config": defaultSuggestionSourceGroupingConfig,
  "topic-workflow-config": defaultTopicWorkflowConfig,
  "trino-session-user-passthrough": false,
  "unified-project-thread-ui": false,
  "use-create-database-v2": false,
  "use-in-house-ck": false,
  "use-only-register-ready-pods": false,
  "use-replica-db-if-available": false,
  "user-context-exhaust-classifier": false,
  "user-memory-extraction": false,
  "user-memory-injection": false,
  "usercheck-domain-denial-config": defaultUsercheckDomainDenialConfig,
};

export const AppFeatureFlagRuntype = R.Union(
  ...(typedObjectKeys(DEFAULT_FLAGS).reduce((acc, flag) => {
    if (!isNewUserFeature(flag)) {
      acc.push(R.Literal(flag));
    }
    return acc;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, [] as any[]) as any),
) as R.Runtype<AppFeatureFlag>;

export { evaluateMinDateFlag } from "./datetimeType.js";

type AppFeatureFlagSet = Omit<FeatureFlagSet, FlaggedNewUserFeature>;

// There are certain flags that correspond to "new user" features, which should
// only be evaluated when a user first logs into Hex. We are generating this
// type that excludes those flags values to ensure that those flags are not
// evaluated outside of first login.
export type AppFeatureFlag = keyof AppFeatureFlagSet;

// These are flags exported to the python kernel and accessible from the Python
// kernel.
export const kernelFeatureFlags = [
  "data-connection-clickhouse-chdb-allowed",
  "data-service-skip-hex-row-index",
  "enable-kernel-write-parquet",
  "semantic-sql-kernel-expansion-enabled",
] as const satisfies (keyof FeatureFlagSet)[];
